import React, { useState, useEffect, useRef, Suspense, lazy, useCallback, useMemo } from 'react';
import { Modal, Button, Form, Spinner, Alert, Nav, Tab, Card, Row, Col, Badge } from 'react-bootstrap';
import DashboardService from '../services/DashboardService';
import CommonStylePanel from './CommonStylePanel';
import ChartPreview from './ChartPreview';
import EventTabPanel from './EventTabPanel';
import { migrateStyleToStandard, collectAllStyleValues, debugStyleStructure } from '../utils/styleMigrationHelper';
import { chartCategories, chartTemplates, generateChartTemplate, getChartIcon, getChartName, getChartDescription } from '../utils/chartTemplates';
import './NLComponentModal.css';
import sanitizeComponentTemplate from '../utils/sanitizeComponentTemplate';
import { useDashboardContext } from '../hooks/useDashboardContext';
import { useGlobalVariables } from '../contexts/GlobalVariablesContext';
import axios from 'axios';

// Monaco Editor를 지연 로딩으로 가져옵니다
const Editor = lazy(() => import('@monaco-editor/react'));

// 간단한 SQL 파서: SELECT ... FROM 구간에서 AS "컬럼" 레이블을 추출하거나, 콤마 분리로 컬럼 수 추정
const deriveColumnsFromSql = (sql) => {
  try {
    if (!sql || typeof sql !== 'string') return [];
    const normalized = sql.replace(/\s+/g, ' ');
    const selectMatch = normalized.match(/select (.+?) from /i);
    if (!selectMatch || !selectMatch[1]) return [];
    const selectPart = selectMatch[1];
    // AS "name" 또는 AS 'name' 또는 AS name 패턴 우선 추출
    const labelRegex = /\bas\s+(["'])(.*?)\1/gi;
    const labels = [];
    let m;
    while ((m = labelRegex.exec(selectPart)) !== null) {
      if (m[2]) labels.push(m[2]);
    }
    if (labels.length > 0) return labels;
    // 큰따옴표 컬럼명 직접 지정 "name" 추출
    const quotedRegex = /\"([^\"]+)\"/g;
    let q;
    while ((q = quotedRegex.exec(selectPart)) !== null) {
      if (q[1]) labels.push(q[1]);
    }
    if (labels.length > 0) return labels;
    // 콤마로 분리하여 대략적인 수만 추정 (함수/서브쿼리 단순화)
    const candidates = selectPart.split(',').map(s => s.trim()).filter(Boolean);
    // 별칭이 있으면 별칭 사용
    return candidates.map(item => {
      const asMatch = item.match(/\bas\s+([a-zA-Z0-9_\"]+)/i);
      if (asMatch) return asMatch[1].replace(/\"/g, '');
      // 마지막 토큰을 컬럼명으로 간주
      const tokens = item.split(/\s+/);
      return tokens[tokens.length - 1].replace(/\"/g, '');
    });
  } catch (_) {
    return [];
  }
};

// existingComponent: 편집 모달이 "기존 배치된 차트"에서 열릴 때 전달되는 전체 템플릿 객체
// - 현행 UX에서는 항상 차트를 먼저 생성한 뒤 편집하므로 보통 존재합니다(업데이트 모드)
// - 향후 "처음부터 LLM으로 신규 생성" 플로우를 사용할 때는 null로 전달될 수 있습니다(생성 모드)
const NLComponentModal = ({ isOpen, onClose, dashboardId, onComponentCreated, existingComponent }) => {
  // 초기 상태 설정 - existingComponent가 있으면 업데이트 모드
  const [title, setTitle] = useState('');
  const [nlRequest, setNlRequest] = useState('');
  const [description, setDescription] = useState('');
  const [updateInterval, setUpdateInterval] = useState(60);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [initialUpdateInterval, setInitialUpdateInterval] = useState(60);
  const [activeTab, setActiveTab] = useState('natural-language');
  const [componentJSON, setComponentJSON] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [showFrame, setShowFrame] = useState(null); // null = 미지정, true/false = 명시적 설정
  const [transparent, setTransparent] = useState(null); // null = 미지정, true/false = 명시적 설정
  const [layoutLocked, setLayoutLocked] = useState(false); // 위치 고정 옵션 추가
  const [isolatedSelection, setIsolatedSelection] = useState(false); // 차트 독립선택 옵션 추가
  // 초기값 비교를 위한 상태 추가
  const [initialShowFrame, setInitialShowFrame] = useState(null);
  const [initialTransparent, setInitialTransparent] = useState(null);
  const [initialLayoutLocked, setInitialLayoutLocked] = useState(false);
  const [initialIsolatedSelection, setInitialIsolatedSelection] = useState(false);
  const [componentStyle, setComponentStyle] = useState({}); // 스타일 상태 추가
  const [initialComponentStyle, setInitialComponentStyle] = useState({}); // 초기 스타일 상태 추가
  const [currentComponent, setCurrentComponent] = useState(null); // 현재 편집 중인 컴포넌트 상태
  const [selectedChartType, setSelectedChartType] = useState(null); // 선택된 차트 타입
  const [chartSelectionMode, setChartSelectionMode] = useState(false); // 차트 선택 모드
  const [legendPosition, setLegendPosition] = useState('bottom'); // 범례 위치 상태 추가
  const [webframeUrl, setWebframeUrl] = useState(''); // 웹프레임 URL 상태 추가
  const [ssoConfig, setSsoConfig] = useState({ enabled: false, method: 'hash', param_name: 'auth_token' }); // SSO 설정 상태 추가
  const [queryParams, setQueryParams] = useState([]); // 쿼리 파라미터 매핑 상태 추가
  const [showAutoComplete, setShowAutoComplete] = useState(null); // 자동완성 표시 상태 (쿼리 파라미터 인덱스)
  const [autoCompleteQuery, setAutoCompleteQuery] = useState({}); // 자동완성 검색어
  const [urlPreview, setUrlPreview] = useState(null); // URL 미리보기
  const [urlPreviewBreakdown, setUrlPreviewBreakdown] = useState(null); // URL 분해 정보
  const [showUrlPreview, setShowUrlPreview] = useState(false); // URL 미리보기 표시 여부
  const [showDescriptionTooltip, setShowDescriptionTooltip] = useState(true); // description 툴팁 표시 여부
  const [progressBarHeight, setProgressBarHeight] = useState(100); // 프로그레스바 높이 (기본값 100%)
  // 비교값 입력 방식 상태 (프로그레스바 전용)
  const [comparisonInputMode, setComparisonInputMode] = useState('constant'); // 'constant' | 'variable'
  const [comparisonConst, setComparisonConst] = useState('');
  const [comparisonVar, setComparisonVar] = useState('');
  // 수동 SQL/정적 텍스트 모드 상태 및 프리뷰
  const USE_NATURAL_TAB_DATASOURCE = true; // 자연어 탭에서 데이터 소스 모드 관리
  const SQL_BLOCKED = new Set(['date_picker', 'webframe']);
  const STATIC_ALLOWED = new Set(['text_label', 'badge', 'kpi', 'progress', 'progress_bar']);
  const [manualMode, setManualMode] = useState('sql'); // 'llm' | 'sql' | 'static' - 수동 SQL 모드로 고정
  const [sqlText, setSqlText] = useState('');
  const [staticText, setStaticText] = useState('');
  const [sqlPreview, setSqlPreview] = useState({ loading: false, error: null, columns: [], rows: [] });
  const [eventConfig, setEventConfig] = useState({
    on_click: null,
    subscribed_variables: null
  }); // 이벤트 설정 상태 추가
  // 이벤트 탭용 테이블 컬럼 로드 상태
  const [eventTableColumns, setEventTableColumns] = useState([]);
  const [eventTableColumnsLoading, setEventTableColumnsLoading] = useState(false);
  const editorRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const resizeTimerRef = useRef(null);
  const initializedRef = useRef(false); // 초기화 상태 추적
  const tableWidthsRef = useRef(null); // 테이블 컬럼 너비 편집 로컬 상태

  // 대시보드 템플릿 및 전역 변수로부터 변수 후보 수집
  const { dashboardTemplate } = useDashboardContext();
  const { variables: globalVars } = useGlobalVariables();
  const availablePublishVars = useMemo(() => {
    const names = new Set();
    try {
      const comps = dashboardTemplate?.components || [];
      comps.forEach((c) => {
        const pub = c?.on_click?.publish;
        if (pub && typeof pub === 'object') {
          Object.keys(pub).forEach((k) => names.add(k));
        }
      });
      if (globalVars && typeof globalVars === 'object') {
        Object.keys(globalVars).forEach((k) => names.add(k));
      }
    } catch (_) {}
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [dashboardTemplate?.components, globalVars]);

  // 테이블 컬럼 (이벤트 편집용 힌트 제공)
  const tableColumns = useMemo(() => {
    try {
      if (eventTableColumns && eventTableColumns.length > 0) return eventTableColumns;
      const cols = existingComponent?.data_result?.columns
        || existingComponent?.chart_options?.data?.columns
        || existingComponent?.chart_options?.options?.columns
        || [];
      return Array.isArray(cols) ? cols : [];
    } catch (_) { return []; }
  }, [existingComponent, eventTableColumns]);

  // viz 타입 유틸 및 가드
  const getViz = useCallback(() => (
    (currentComponent?.chart_options?.viz_type
      || existingComponent?.chart_options?.viz_type
      || selectedChartType
      || '')
      .toLowerCase()
  ), [currentComponent, existingComponent, selectedChartType]);
  const canUseSql = useCallback(() => !SQL_BLOCKED.has(getViz()), [getViz]);
  const canUseStatic = useCallback(() => STATIC_ALLOWED.has(getViz()), [getViz]);

  // CommonStylePanel에 전달할 style prop (메모이제이션)
  const styleForCommonPanel = useMemo(() => ({
    ...componentStyle,
    // 레거시 advanced 속성도 전달 (CommonStylePanel이 읽기 위해)
    advanced: currentComponent?.chart_options?.options?.advanced || existingComponent?.chart_options?.options?.advanced
  }), [componentStyle, currentComponent?.chart_options?.options?.advanced, existingComponent?.chart_options?.options?.advanced]);

  // 초기 로드 시 수동 모드/값 세팅
  useEffect(() => {
    if (!isOpen) return;
    try {
      const t = existingComponent?.data_source?.type;
      const initialSql = existingComponent?.data_source?.query || existingComponent?.data_source?.sql || '';
      const initialStatic = existingComponent?.data_source?.content || existingComponent?.chart_options?.options?.content || '';
      setSqlText(typeof initialSql === 'string' ? initialSql : '');
      setStaticText(typeof initialStatic === 'string' ? initialStatic : '');
      if (t === 'static' && canUseStatic()) setManualMode('static');
      else if (t === 'sql' && canUseSql()) setManualMode('sql');
      else setManualMode('llm');
    } catch (_) {
      setManualMode('llm');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // viz 변경 시 불가 모드 자동 복귀
  useEffect(() => {
    const viz = getViz();
    if (manualMode === 'static' && !STATIC_ALLOWED.has(viz)) {
      setManualMode('llm');
    }
    if (manualMode === 'sql' && SQL_BLOCKED.has(viz)) {
      setManualMode('llm');
    }
  }, [manualMode, getViz]);

  // 이벤트 탭 진입 시 테이블 컬럼을 백엔드에서 조회하여 동기화
  useEffect(() => {
    const viz = existingComponent?.chart_options?.viz_type?.toLowerCase();
    const sql = existingComponent?.data_source?.query;
    if (!isOpen || activeTab !== 'events' || viz !== 'table' || !sql) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setEventTableColumnsLoading(true);
        const resp = await DashboardService.updateChartData({
          sql,
          chart_template: existingComponent?.chart_options || {}
        });
        if (!cancelled && resp?.success) {
          const cols = resp.data?.chart_data?.data?.columns || resp.data?.data?.columns || resp.data?.columns || [];
          if (Array.isArray(cols) && cols.length > 0) {
            setEventTableColumns(cols);
          } else {
            const derived = deriveColumnsFromSql(sql);
            if (Array.isArray(derived) && derived.length > 0) {
              setEventTableColumns(derived);
            }
          }
        }
      } catch (_) {
        if (!cancelled) {
          const derived = deriveColumnsFromSql(sql);
          if (Array.isArray(derived) && derived.length > 0) {
            setEventTableColumns(derived);
          }
        }
      } finally {
        if (!cancelled) setEventTableColumnsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, activeTab, existingComponent]);

  // 스타일 탭 진입 시(테이블 viz)에도 컬럼 목록 동기화
  useEffect(() => {
    const viz = (currentComponent?.chart_options?.viz_type || existingComponent?.chart_options?.viz_type || currentComponent?.viz_type || existingComponent?.viz_type || '').toLowerCase();
    const sql = currentComponent?.data_source?.query || existingComponent?.data_source?.query;
    if (!isOpen || activeTab !== 'style-editor' || viz !== 'table' || !sql) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setEventTableColumnsLoading(true);
        const baseTemplate = currentComponent || existingComponent || {};
        const resp = await DashboardService.updateChartData({
          sql,
          chart_template: baseTemplate
        });
        if (!cancelled && resp?.success) {
          const cols = resp.data?.chart_data?.data?.columns || resp.data?.data?.columns || resp.data?.columns || [];
          if (Array.isArray(cols) && cols.length > 0) {
            setEventTableColumns(cols);
          } else {
            const derived = deriveColumnsFromSql(sql);
            if (Array.isArray(derived) && derived.length > 0) {
              setEventTableColumns(derived);
            }
          }
        }
      } catch (_) {
        if (!cancelled) {
          const derived = deriveColumnsFromSql(sql);
          if (Array.isArray(derived) && derived.length > 0) {
            setEventTableColumns(derived);
          }
        }
      } finally {
        if (!cancelled) setEventTableColumnsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, activeTab, currentComponent, existingComponent]);

  // Monaco Editor 레이아웃 수동 관리 (ResizeObserver 에러 회피)
  const handleEditorResize = useCallback(() => {
    if (editorRef.current) {
      requestAnimationFrame(() => {
        try {
          editorRef.current.layout();
        } catch (e) {
          if (!e?.message || !e.message.includes('ResizeObserver')) {
            console.error(e);
          }
        }
      });
    }
  }, []);
  
  // 템플릿 탭 진입 시 에디터 레이아웃 재계산 (마지막 줄 잘림 방지)
  useEffect(() => {
    if (activeTab === 'template-editor' && editorRef.current) {
      // 모달/탭 전환 후 약간의 지연 뒤 강제 레이아웃
      const timer = setTimeout(() => {
        handleEditorResize();
      }, 60);

      const handleWindowResize = () => {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = setTimeout(() => {
          handleEditorResize();
        }, 120);
      };
      window.addEventListener('resize', handleWindowResize);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', handleWindowResize);
      };
    }
  }, [activeTab]);
  
  // 템플릿 JSON 스키마 정의
  const componentTemplateSchema = {
    type: "object",
    required: ["id", "title"],
    properties: {
      id: { type: "string" },
      title: { type: "string" },
      position: {
        type: "object",
        properties: {
          x: { type: "number" },
          y: { type: "number" },
          w: { type: "number" },
          h: { type: "number" }
        }
      },
      chart_options: { 
        type: "object",
        properties: {
          showFrame: { type: "boolean" },
          transparent: { type: "boolean" }
        }
      },
      data_source: { type: "object" },
      updateInterval: { type: "number" },
      description: { type: "string" },
      layout_locked: { type: "boolean" } // 위치 고정 옵션 추가
    }
  };

  // existingComponent가 변경되면 폼 데이터 초기화 (한 번만 실행)
  useEffect(() => {
    if (existingComponent && isOpen && !initializedRef.current) {
      initializedRef.current = true;
      setIsUpdateMode(true);
      setTitle(existingComponent.title || ''); // title 초기화
      setNlRequest(''); // 자연어 요청은 히스토리가 없으므로 빈 값으로 시작
      setDescription(existingComponent.description || '');
      const interval = existingComponent.updateInterval || 3600;
      setUpdateInterval(interval);
      setInitialUpdateInterval(interval);
      
      // 이벤트 설정 로드
      setEventConfig({
        on_click: existingComponent.on_click || null,
        subscribed_variables: existingComponent.subscribed_variables || null,
        touched_on_click: false,
        touched_subscribed: false
      });
      // showFrame 옵션 로드 - 명시적으로 설정된 경우만 값 설정, 아니면 null(미지정)
      const initialShowFrameValue = (existingComponent.chart_options?.showFrame !== undefined)
        ? existingComponent.chart_options.showFrame
        : null; // 미지정 상태
      setShowFrame(initialShowFrameValue);
      
      // transparent 옵션 로드 - 명시적으로 설정된 경우만 값 설정, 아니면 null(미지정)
      const initialTransparentValue = (existingComponent.chart_options?.transparent !== undefined)
        ? existingComponent.chart_options.transparent
        : null; // 미지정 상태
      setTransparent(initialTransparentValue);
      // 위치 고정 옵션 로드
      setLayoutLocked(existingComponent.layout_locked === true);
      // 차트 독립선택 옵션 로드
      setIsolatedSelection(existingComponent.isolatedSelection === true);
      // 범례 위치 옵션 로드
      const existingLegendPosition = existingComponent.chart_options?.options?.advanced?.legendPosition || 'bottom';
      setLegendPosition(existingLegendPosition);
      
      // 웹프레임 URL 로드 (표준 위치: data_source.url)
      if (existingComponent.chart_options?.viz_type === 'webframe') {
        const url = existingComponent.data_source?.url || '';
        setWebframeUrl(url);
        
        // SSO 설정 로드
        const sso = existingComponent.data_source?.sso_config || { enabled: false, method: 'hash', param_name: 'auth_token' };
        setSsoConfig(sso);
        
        // 쿼리 파라미터 매핑 로드
        const params = existingComponent.data_source?.query_params || [];
        setQueryParams(params);
      }
      
      // showDescriptionTooltip 옵션 로드 (기본값: true)
      const existingShowDescTooltip = existingComponent.chart_options?.options?.advanced?.showDescriptionTooltip;
      setShowDescriptionTooltip(existingShowDescTooltip !== false); // undefined나 true일 때는 true
      
      // 프로그레스바 높이 옵션 로드 (기본값: 100%)
      if (existingComponent.chart_options?.viz_type === 'progress_bar') {
        const barHeightPercentage = existingComponent.chart_options?.options?.advanced?.barHeightPercentage || 100;
        setProgressBarHeight(barHeightPercentage);
        // 비교값 초기 모드/값 설정
        const cv = existingComponent.chart_options?.options?.advanced?.comparisonValue;
        if (typeof cv === 'string' && cv.trim().startsWith(':')) {
          setComparisonInputMode('variable');
          setComparisonVar(cv.trim().slice(1));
          setComparisonConst('');
        } else if (typeof cv === 'number' || (typeof cv === 'string' && cv.trim() !== '')) {
          const n = typeof cv === 'number' ? cv : (parseFloat(cv) || 0);
          setComparisonInputMode('constant');
          setComparisonConst(String(n));
          setComparisonVar('');
        } else {
          setComparisonInputMode('constant');
          setComparisonConst('');
          setComparisonVar('');
        }
      }
      
      // 초기값 비교를 위한 상태 업데이트: 실제 초기 표시값을 기준으로 설정
      setInitialShowFrame(initialShowFrameValue);
      setInitialTransparent(initialTransparentValue);
      setInitialLayoutLocked(existingComponent.layout_locked === true);
      setInitialIsolatedSelection(existingComponent.isolatedSelection === true);
      
      // 스타일 정보 로드 (레거시 마이그레이션 포함)
      const chartType = existingComponent.chart_options?.viz_type?.toLowerCase() || 'bar';
      
      // 레거시 스타일을 표준 형식으로 마이그레이션
      const migratedOptions = migrateStyleToStandard(
        existingComponent.chart_options?.options || {},
        chartType
      );
      
      // 마이그레이션된 스타일 사용 (visibility와 advanced 포함)
      const existingStyle = migratedOptions.style || {};
      // visibility 정보가 있으면 복원
      if (existingComponent.chart_options?.options?.style?.visibility) {
        existingStyle.visibility = existingComponent.chart_options.options.style.visibility;
      }
      // advanced 옵션은 componentStyle에 포함하지 않음 (별도로 관리)
      // advanced 옵션은 legendPosition, showDescriptionTooltip 등으로 개별 관리됨
      
      debugStyleStructure(existingComponent.chart_options?.options, chartType);
      
      setComponentStyle(existingStyle);  // advanced가 제외된 스타일만 저장
      setInitialComponentStyle(existingStyle); // 초기 스타일 저장
      
      // currentComponent도 마이그레이션된 옵션으로 업데이트
      const updatedComponent = {
        ...existingComponent,
        chart_options: {
          ...existingComponent.chart_options,
          options: migratedOptions
        }
      };
      setCurrentComponent(updatedComponent); // 마이그레이션된 컴포넌트 저장
      
      // 템플릿 에디터를 위한 JSON 문자열 설정
      try {
        setComponentJSON(JSON.stringify(existingComponent, null, 2));
      } catch (err) {
        console.error('컴포넌트 JSON 변환 오류:', err);
        setComponentJSON('');
      }
    } else {
      setIsUpdateMode(false);
      resetForm();
      // 새 컴포넌트 생성 시 미지정 상태로 설정 (LLM이 결정하도록)
      setShowFrame(null);
      setTransparent(null);
      setInitialShowFrame(null);
      setInitialTransparent(null);
    }
  }, [existingComponent, isOpen]);

  // 모달이 열리거나 닫힐 때 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      // 모달이 닫힐 때 상태 초기화
      setError(null);
      setJsonError(null);
      initializedRef.current = false; // 다음에 열릴 때를 위해 초기화 플래그 리셋
    } else if (!existingComponent) {
      // 새 컴포넌트 생성 모드일 때 차트 선택 모드를 기본적으로 활성화
      setChartSelectionMode(true);
      initializedRef.current = false;
    }
  }, [isOpen]); // existingComponent를 의존성에서 제거
  
  // showFrame이나 transparent 변경 시 currentComponent 업데이트
  useEffect(() => {
    if (currentComponent && isUpdateMode) {
      const updatedComponent = { ...currentComponent };
      
      if (updatedComponent.chart_options) {
        // showFrame 처리
        if (showFrame === null) {
          delete updatedComponent.chart_options.showFrame;
        } else {
          updatedComponent.chart_options.showFrame = showFrame;
        }
        
        // transparent 처리
        if (transparent === null) {
          delete updatedComponent.chart_options.transparent;
        } else {
          updatedComponent.chart_options.transparent = transparent;
        }
        
        setCurrentComponent(updatedComponent);
        setComponentJSON(JSON.stringify(updatedComponent, null, 2));
      }
    }
  }, [showFrame, transparent]); // isUpdateMode와 currentComponent는 의존성에서 제외 (무한 루프 방지)

  // 템플릿 편집 함수 (Monaco Editor용)
  const handleTemplateEdit = (value) => {
    // 디바운스 타이머가 이미 있으면 취소
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // 상태 업데이트를 디바운싱 (300ms)
    debounceTimerRef.current = setTimeout(() => {
      setComponentJSON(value);
      // JSON 유효성 검증
      try {
        const parsed = JSON.parse(value);
        setJsonError(null);
        // 파싱 성공 시 현재 컴포넌트 상태도 업데이트
        setCurrentComponent(parsed);
        
        // showFrame과 transparent 상태 동기화
        if (parsed.chart_options) {
          // showFrame이 명시되어 있으면 해당 값으로, 없으면 null(미지정)
          setShowFrame(parsed.chart_options.showFrame !== undefined ? parsed.chart_options.showFrame : null);
          // transparent가 명시되어 있으면 해당 값으로, 없으면 null(미지정)
          setTransparent(parsed.chart_options.transparent !== undefined ? parsed.chart_options.transparent : null);
        }
      } catch (err) {
        setJsonError('JSON 형식이 올바르지 않습니다.');
      }
    }, 300);
  };

  // 코드 자동 포맷팅 함수
  const formatCode = () => {
    if (editorRef.current) {
      editorRef.current.getAction('editor.action.formatDocument').run();
    }
  };

  // 배경색 변경 핸들러 (options.style.background.color에 반영)
  const handleBackgroundColorChange = (newColor) => {
    // componentStyle 업데이트: background.color 보장
    const updatedStyle = {
      ...componentStyle,
      background: {
        ...(componentStyle?.background || {}),
        color: newColor
      }
    };
    setComponentStyle(updatedStyle);

    // currentComponent 동기화 (에디터 JSON도 함께 갱신)
    if (currentComponent) {
      const { advanced, visibility, ...styleWithoutSpecial } = updatedStyle;
      const updatedComponent = {
        ...currentComponent,
        chart_options: {
          ...currentComponent.chart_options,
          options: {
            ...currentComponent.chart_options?.options,
            style: {
              ...styleWithoutSpecial,
              ...(visibility && { visibility })
            },
            ...(advanced && { advanced: {
              ...currentComponent.chart_options?.options?.advanced,
              ...advanced
            }})
          }
        }
      };

      // text_label의 레거시 호환: 배경색을 flat 옵션에도 반영
      try {
        const chartType = (currentComponent?.viz_type || currentComponent?.chart_options?.viz_type || '').toLowerCase();
        if (chartType === 'text_label') {
          if (!updatedComponent.chart_options.options) {
            updatedComponent.chart_options.options = {};
          }
          // transparent가 true가 아니면 flat backgroundColor에 반영
          const isTransparent = transparent === true;
          updatedComponent.chart_options.options.backgroundColor = isTransparent ? 'transparent' : newColor;
        }
      } catch (_) {}

      // showFrame/transparent 상태 유지 반영
      if (showFrame === null) {
        delete updatedComponent.chart_options.showFrame;
      } else {
        updatedComponent.chart_options.showFrame = showFrame;
      }
      if (transparent === null) {
        delete updatedComponent.chart_options.transparent;
      } else {
        updatedComponent.chart_options.transparent = transparent;
      }

      setCurrentComponent(updatedComponent);
      try {
        setComponentJSON(JSON.stringify(updatedComponent, null, 2));
      } catch (e) {
        // no-op: JSON 변환 실패 시 무시
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // 현재 활성 탭이 템플릿 에디터인 경우
      if (activeTab === 'template-editor') {
        try {
          const componentTemplate = JSON.parse(componentJSON);
          
          // 기본 필수 필드 검증
          if (!componentTemplate.id || !componentTemplate.title) {
            throw new Error('컴포넌트 템플릿에는 id와 title이 필수입니다.');
          }
          
          // 템플릿 에디터에서는 JSON에서 직접 모든 값을 가져옴
          // description과 updateInterval은 JSON에서 직접 편집
          
          // 컴포넌트 적용
          onComponentCreated(componentTemplate, isUpdateMode);
          resetForm();
          onClose();
          return;
        } catch (err) {
          setError(err.message || 'JSON 파싱 중 오류가 발생했습니다.');
          setLoading(false);
          return;
        }
      }
      
      // 템플릿 에디터 탭에서 직접 수정한 경우 (activeTab이 'template-editor'가 아닌 경우를 처리)
      // 또는 스타일 에디터 탭이거나 옵션만 변경되었고 자연어가 비어있는 경우
      // 추가: 자연어 탭 수동 모드(sql/static) 저장 경로
      if (isUpdateMode && existingComponent && !nlRequest.trim()) {

        // 자연어 탭에서 수동 모드로 저장하는 경우를 최우선 처리
        if (activeTab === 'natural-language') {
          const base = currentComponent || existingComponent;
          if (manualMode === 'sql') {
            if (!canUseSql()) {
              setError('수동 SQL은 이 차트에서 지원되지 않습니다.');
              setLoading(false);
              return;
            }
            
            // 스타일 준비 (componentStyle이 있으면 우선 사용)
            const hasComponentStyle = componentStyle && Object.keys(componentStyle).length > 0;
            const latestStyle = hasComponentStyle ? componentStyle : base?.chart_options?.options?.style;
            const { advanced: styleAdvanced, ...cleanComponentStyle } = latestStyle || {};

            const updatedComponent = {
              ...base,
              data_source: {
                ...(base?.data_source || {}),
                type: 'sql',
                query: sqlText
              },
              chart_options: {
                ...(base?.chart_options || {}),
                options: {
                  ...(base?.chart_options?.options || {}),
                  style: cleanComponentStyle,
                  advanced: {
                    ...(base?.chart_options?.options?.advanced || {}),
                    ...(styleAdvanced || {}),
                    showDescriptionTooltip: showDescriptionTooltip,
                    legendPosition: legendPosition
                  }
                }
              },
              title: title || base.title,
              description: description || base.description,
              updateInterval: parseInt(updateInterval, 10),
              layout_locked: layoutLocked,
              isolatedSelection: isolatedSelection
            };
            
            // 프레임 및 투명도 설정 적용
            if (showFrame !== null) updatedComponent.chart_options.showFrame = showFrame;
            if (transparent !== null) updatedComponent.chart_options.transparent = transparent;
            
            if (typeof onComponentCreated === 'function') {
              onComponentCreated(updatedComponent, true);
            }
            resetForm();
            onClose();
            return;
          }
          if (manualMode === 'static') {
            if (!canUseStatic()) {
              setError('정적 텍스트는 이 차트에서 지원되지 않습니다.');
              setLoading(false);
              return;
            }
            const content = staticText;
            
            // 스타일 준비 (componentStyle이 있으면 우선 사용)
            const hasComponentStyle = componentStyle && Object.keys(componentStyle).length > 0;
            const latestStyle = hasComponentStyle ? componentStyle : base?.chart_options?.options?.style;
            const { advanced: styleAdvanced, ...cleanComponentStyle } = latestStyle || {};

            const updatedComponent = {
              ...base,
              data_source: {
                ...(base?.data_source || {}),
                type: 'static',
                content
              },
              chart_options: {
                ...(base?.chart_options || {}),
                options: {
                  ...(base?.chart_options?.options || {}),
                  content,
                  style: cleanComponentStyle,
                  advanced: {
                    ...(base?.chart_options?.options?.advanced || {}),
                    ...(styleAdvanced || {}),
                    showDescriptionTooltip: showDescriptionTooltip,
                    legendPosition: legendPosition
                  }
                }
              },
              title: title || base.title,
              updateInterval: parseInt(updateInterval, 10),
              layout_locked: layoutLocked,
              isolatedSelection: isolatedSelection
            };
            
            // 프레임 및 투명도 설정 적용
            if (showFrame !== null) updatedComponent.chart_options.showFrame = showFrame;
            if (transparent !== null) updatedComponent.chart_options.transparent = transparent;

            if (typeof onComponentCreated === 'function') {
              onComponentCreated(updatedComponent, true);
            }
            resetForm();
            onClose();
            return;
          }
        }
        
        // 최신 컴포넌트 상태 사용
        let baseComponent = currentComponent || existingComponent;
        
        // 템플릿 에디터에서 수정한 JSON이 있으면 우선 적용
        if (componentJSON) {
          try {
            const parsedComponent = JSON.parse(componentJSON);
            // 템플릿 에디터에서 수정한 내용을 기본으로 사용
            baseComponent = parsedComponent;
          } catch (e) {
            console.error('[Template/Style Update] Failed to parse component JSON:', e);
            // 파싱 실패 시 기존 컴포넌트 사용
          }
        }
        
        // 이벤트 설정 반영: null(삭제)도 반드시 반영되도록 항상 덮어쓰기
        // 이벤트 설정 병합: 사용자가 실제로 편집했을 때만 덮어쓰기
        if (eventConfig && eventConfig.touched_on_click) {
          // 빈 객체는 null로 처리하지 않고, 명시적 null은 삭제 의도로 인정
          const nextOnClick = (Object.prototype.hasOwnProperty.call(eventConfig, 'on_click')) ? eventConfig.on_click : baseComponent.on_click;
          baseComponent = { ...baseComponent, on_click: nextOnClick };
        }
        if (eventConfig && eventConfig.touched_subscribed) {
          const nextSubscribed = (Object.prototype.hasOwnProperty.call(eventConfig, 'subscribed_variables')) ? eventConfig.subscribed_variables : baseComponent.subscribed_variables;
          baseComponent = { ...baseComponent, subscribed_variables: nextSubscribed };
        }
        
        // 기존 컴포넌트를 복제하고 변경된 옵션들만 업데이트
        const updatedComponent = {
          ...baseComponent,
          title: title || baseComponent.title, // title 업데이트
          updateInterval: parseInt(updateInterval, 10),
          layout_locked: layoutLocked, // 위치 고정 옵션 포함
          isolatedSelection: isolatedSelection // 차트 독립선택 옵션 포함
        };
        
        // 프레임 표시 옵션도 업데이트
        if (updatedComponent.chart_options) {
          // showFrame 처리: null이면 키 삭제, 아니면 값 설정
          if (showFrame === null) {
            delete updatedComponent.chart_options.showFrame;
          } else {
            updatedComponent.chart_options.showFrame = showFrame;
          }
          
          // transparent 처리: null이면 키 삭제, 아니면 값 설정
          if (transparent === null) {
            delete updatedComponent.chart_options.transparent;
          } else {
            updatedComponent.chart_options.transparent = transparent;
          }
          
          // 차트 타입 확인
          const chartType = updatedComponent.chart_options.viz_type?.toLowerCase() || 'bar';
          
          // 기존 options를 마이그레이션
          const migratedOptions = migrateStyleToStandard(
            updatedComponent.chart_options.options || {},
            chartType
          );
          
          // 마이그레이션된 options에 새로운 스타일 적용
          // 레거시 속성은 이미 제거되었고, style만 유지됨
          // DatePicker의 경우 advanced 옵션이 componentStyle에 포함되어 있음
          if (chartType === 'date_picker') {
            // componentStyle이 CommonStylePanel에서 변경한 최신 값이므로 우선적으로 사용
            // 단, componentStyle이 비어있지 않은 경우에만 사용
            const hasComponentStyle = componentStyle && Object.keys(componentStyle).length > 0;
            const latestStyle = hasComponentStyle ? componentStyle : (baseComponent?.chart_options?.options?.style || {});
            
            // advanced 속성 분리
            const { advanced: styleAdvanced, ...cleanComponentStyle } = latestStyle;
            updatedComponent.chart_options.options = {
              ...migratedOptions,  // 마이그레이션된 options (레거시 제거됨)
              style: {
                fonts: cleanComponentStyle.fonts || {}, // fonts 저장
                visibility: cleanComponentStyle.visibility || {}, // visibility도 함께 저장 (제목 토글 등)
                background: cleanComponentStyle.background || {} // background도 포함
              },
              advanced: {
                ...migratedOptions.advanced,
                ...styleAdvanced, // DatePicker의 advanced 옵션
                legendPosition: legendPosition, // 범례 위치 추가
                showDescriptionTooltip: showDescriptionTooltip // description 툴팁 표시 여부
              }
            };
          } else {
            // componentStyle이 CommonStylePanel에서 변경한 최신 값이므로 우선적으로 사용
            const latestStyle = componentStyle || baseComponent?.chart_options?.options?.style;
            // advanced 속성 제거 (style에 포함되면 안됨)
            const { advanced: styleAdvanced, ...cleanComponentStyle } = latestStyle;
            updatedComponent.chart_options.options = {
              ...migratedOptions,  // 마이그레이션된 options (레거시 제거됨)
              style: cleanComponentStyle,  // advanced를 제외한 스타일만 저장
              advanced: {
                ...migratedOptions.advanced,
                ...styleAdvanced, // componentStyle의 advanced (있다면)
                legendPosition: legendPosition, // 범례 위치 추가
                showDescriptionTooltip: showDescriptionTooltip // description 툴팁 표시 여부
              }
            };
            // text_label 렌더러는 flat 옵션을 참조하므로 스타일을 flat 옵션으로 동기화
            if (chartType === 'text_label') {
              try {
                const style = updatedComponent.chart_options.options.style || {};
                const fonts = style.fonts?.value || {};
                const background = style.background || {};
                const flat = updatedComponent.chart_options.options;
                if (fonts.fontFamily !== undefined) flat.fontFamily = fonts.fontFamily;
                if (fonts.fontSize !== undefined) flat.fontSize = `${String(fonts.fontSize).replace('px','')}px`;
                if (fonts.fontWeight !== undefined) flat.fontWeight = fonts.fontWeight;
                if (fonts.fontStyle !== undefined) flat.fontStyle = fonts.fontStyle;
                if (fonts.color !== undefined) flat.textColor = fonts.color;
                if (fonts.lineHeight !== undefined) flat.lineHeight = String(fonts.lineHeight);
                // transparent가 true면 배경 투명, 아니면 선택 색상 적용
                if (transparent === true) {
                  flat.backgroundColor = 'transparent';
                } else if (background.color !== undefined) {
                  flat.backgroundColor = background.color;
                }
              } catch (_) {}
            }
          }
          
        } else {
          // componentStyle이 CommonStylePanel에서 변경한 최신 값이므로 우선적으로 사용
          const latestStyle = componentStyle || baseComponent?.chart_options?.options?.style;
          updatedComponent.chart_options = { 
            options: { style: latestStyle }
          };
          // showFrame이 명시적으로 설정된 경우만 추가
          if (showFrame !== null) {
            updatedComponent.chart_options.showFrame = showFrame;
          }
          // transparent가 명시적으로 설정된 경우만 추가
          if (transparent !== null) {
            updatedComponent.chart_options.transparent = transparent;
          }
        }
        
        // 웹프레임 차트인 경우 URL 업데이트 (표준 위치: data_source.url)
        if (updatedComponent.chart_options?.viz_type === 'webframe' && webframeUrl) {
          // data_source 구조를 표준화
          if (!updatedComponent.data_source) {
            updatedComponent.data_source = {
              type: 'static',
              url: webframeUrl,
              sso_config: ssoConfig,
              query_params: queryParams,
              content: {
                viz_type: 'webframe'
              }
            };
          } else {
            // URL, SSO 설정, 쿼리 파라미터 저장
            updatedComponent.data_source.url = webframeUrl;
            updatedComponent.data_source.type = 'static';
            updatedComponent.data_source.sso_config = ssoConfig;
            updatedComponent.data_source.query_params = queryParams;
            
            // content는 viz_type만 포함
            if (!updatedComponent.data_source.content) {
              updatedComponent.data_source.content = {
                viz_type: 'webframe'
              };
            } else {
              updatedComponent.data_source.content.viz_type = 'webframe';
              // 중복된 URL 필드 제거
              delete updatedComponent.data_source.content.data;
            }
          }
          
          // chart_options.data.url 제거 (중복 제거)
          if (updatedComponent.chart_options?.data?.url) {
            delete updatedComponent.chart_options.data.url;
            // data 객체가 비어있으면 제거
            if (Object.keys(updatedComponent.chart_options.data).length === 0) {
              delete updatedComponent.chart_options.data;
            }
          }
        }
        
        // 백엔드 호출 없이 로컬에서 컴포넌트 업데이트
        if (typeof onComponentCreated === 'function') {
          onComponentCreated(updatedComponent, true);
        } else {
          console.error('[Template/Style Update] onComponentCreated is not a function!', onComponentCreated);
        }
        
        resetForm();
        onClose();
        return; // 중요: 여기서 리턴하여 자연어 처리 로직으로 가지 않도록 함
      } else if (selectedChartType && currentComponent && !isUpdateMode) {
        // 차트 템플릿을 선택한 경우 - LLM 호출 없이 직접 추가
        // currentComponent는 이미 generateChartTemplate으로 생성된 완전한 템플릿
        const { data_result, ...componentWithoutDataResult } = currentComponent;
        const finalComponent = {
          ...componentWithoutDataResult,
          title: title || componentWithoutDataResult.title || '새 차트', // 사용자가 입력한 title 사용
          updateInterval: parseInt(updateInterval, 10),
          layout_locked: layoutLocked,
          isolatedSelection: isolatedSelection,
          description: description || currentComponent.description || ''
        };
        
        // showFrame과 transparent 옵션 적용
        if (finalComponent.chart_options) {
          if (showFrame !== null) {
            finalComponent.chart_options.showFrame = showFrame;
          }
          if (transparent !== null) {
            finalComponent.chart_options.transparent = transparent;
          }
          // showDescriptionTooltip 옵션 추가
          if (!finalComponent.chart_options.options) {
            finalComponent.chart_options.options = {};
          }
          if (!finalComponent.chart_options.options.advanced) {
            finalComponent.chart_options.options.advanced = {};
          }
          finalComponent.chart_options.options.advanced.showDescriptionTooltip = showDescriptionTooltip;
        }
        
        // 웹프레임 차트인 경우 URL 및 SSO 설정 (표준 위치: data_source)
        if (selectedChartType === 'webframe' && webframeUrl) {
          // data_source를 표준 구조로 설정
          finalComponent.data_source = {
            type: 'static',
            url: webframeUrl,
            sso_config: ssoConfig,
            query_params: queryParams,
            content: {
              viz_type: 'webframe'
            }
          };
          
          // chart_options.data 제거 (중복 방지)
          if (finalComponent.chart_options?.data) {
            delete finalComponent.chart_options.data;
          }
        }
        
        // 컴포넌트 생성 콜백 호출 (LLM 호출 없이)
        onComponentCreated(finalComponent, false);
        resetForm();
        onClose();
        return;
      } else {
        // 자연어 요청 검증
        if (!nlRequest.trim()) {
          setError('자연어 요청을 입력하거나 차트 종류를 선택해주세요.');
          setLoading(false);
          return;
        }
        
        // 자연어 요청이 있는 경우 기존 로직대로 진행
        let result;
        
        if (isUpdateMode && existingComponent) {
          // 업데이트 모드: 기존 템플릿(전체)을 LLM에 전달하되, 런타임/대용량/디버그 필드만 최소 정제하여 전송
          // - 재구성(축약)하지 않고 원본 구조와 모든 설정성 필드를 보존
          // - 향후 새로운 필드/태그가 추가되어도 자동으로 포함됨
          const sanitizedFullTemplate = sanitizeComponentTemplate({
            // title은 사용자가 수정했을 수 있으므로 반영
            ...existingComponent,
            title: title || existingComponent.title,
            // 위치 고정 속성은 현재 토글 값을 반영해 준다
            layout_locked: layoutLocked,
            isolatedSelection: isolatedSelection
          });

          result = await DashboardService.createDashboardComponent(
            nlRequest,
            updateInterval,
            sanitizedFullTemplate // 최소 정제된 전체 템플릿 전달
          );
          
          // 실제 응답 로깅 추가
          // 응답 처리 개선
          if (result.success) {
            if (result.data && result.data.component_template) {
              // LLM 응답의 showFrame 값을 그대로 사용 (LLM이 사용자 요청을 해석한 결과)
              const finalComponent = {
                ...result.data.component_template,
                title: title || result.data.component_template.title, // 사용자가 입력한 title 우선 사용
                layout_locked: layoutLocked, // 위치 고정 옵션 포함
                isolatedSelection: isolatedSelection // 차트 독립선택 옵션 포함
              };
              
              // LLM 응답에 showFrame이 없고, UI에서 명시적으로 설정한 경우만 추가
              if (finalComponent.chart_options && finalComponent.chart_options.showFrame === undefined && showFrame !== null) {
                finalComponent.chart_options.showFrame = showFrame;
              } else if (finalComponent.chart_options?.showFrame !== undefined) {
              }
              // LLM 응답에 transparent가 없고, UI에서 명시적으로 설정한 경우만 추가
              if (finalComponent.chart_options && finalComponent.chart_options.transparent === undefined && transparent !== null) {
                finalComponent.chart_options.transparent = transparent;
              } else if (finalComponent.chart_options?.transparent !== undefined) {
              }
              
              // showDescriptionTooltip 옵션 적용 (UI에서 설정한 값 반영)
              if (finalComponent.chart_options) {
                if (!finalComponent.chart_options.options) {
                  finalComponent.chart_options.options = {};
                }
                if (!finalComponent.chart_options.options.advanced) {
                  finalComponent.chart_options.options.advanced = {};
                }
                finalComponent.chart_options.options.advanced.showDescriptionTooltip = showDescriptionTooltip;
              }
              
              onComponentCreated(finalComponent, isUpdateMode);
              resetForm();
              onClose();
            } else {
              console.error('[NLComponentModal] Success response but no component_template:', result);
              setError('컴포넌트 템플릿을 받아오지 못했습니다 (응답 구조 오류)');
            }
          } else {
            // API 호출 실패 시 실제 오류 메시지 표시
            console.error('[NLComponentModal] API call failed:', result);
            setError(result.error || '컴포넌트 업데이트 중 오류가 발생했습니다');
          }
        } else {
          // 생성 모드: 새 컴포넌트 생성
          // title이 있으면 템플릿으로 전달
          const newComponentTemplate = title ? { title } : null;
          
          result = await DashboardService.createDashboardComponent(
            nlRequest,
            updateInterval,
            newComponentTemplate
          );
          
          // 응답 처리 개선
          if (result.success) {
            if (result.data && result.data.component_template) {
              // LLM 응답의 showFrame 값을 그대로 사용 (LLM이 사용자 요청을 해석한 결과)
              const finalComponent = {
                ...result.data.component_template,
                title: title || result.data.component_template.title, // 사용자가 입력한 title 우선 사용
                layout_locked: layoutLocked, // 위치 고정 옵션 포함
                isolatedSelection: isolatedSelection // 차트 독립선택 옵션 포함
              };
              
              // LLM 응답에 showFrame이 없고, UI에서 명시적으로 설정한 경우만 추가
              if (finalComponent.chart_options && finalComponent.chart_options.showFrame === undefined && showFrame !== null) {
                finalComponent.chart_options.showFrame = showFrame;
              } else if (finalComponent.chart_options?.showFrame !== undefined) {
              }
              // LLM 응답에 transparent가 없고, UI에서 명시적으로 설정한 경우만 추가
              if (finalComponent.chart_options && finalComponent.chart_options.transparent === undefined && transparent !== null) {
                finalComponent.chart_options.transparent = transparent;
              } else if (finalComponent.chart_options?.transparent !== undefined) {
              }
              
              // showDescriptionTooltip 옵션 적용 (UI에서 설정한 값 반영)
              if (finalComponent.chart_options) {
                if (!finalComponent.chart_options.options) {
                  finalComponent.chart_options.options = {};
                }
                if (!finalComponent.chart_options.options.advanced) {
                  finalComponent.chart_options.options.advanced = {};
                }
                finalComponent.chart_options.options.advanced.showDescriptionTooltip = showDescriptionTooltip;
              }
              
              onComponentCreated(finalComponent, isUpdateMode);
              resetForm();
              onClose();
            } else {
              console.error('[NLComponentModal] Success response but no component_template:', result);
              setError('컴포넌트 템플릿을 받아오지 못했습니다 (응답 구조 오류)');
            }
          } else {
            // API 호출 실패 시 실제 오류 메시지 표시
            console.error('[NLComponentModal] API call failed:', result);
            setError(result.error || '컴포넌트 생성 중 오류가 발생했습니다');
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || '컴포넌트 처리 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setNlRequest('');
    setDescription('');
    setUpdateInterval(60);
    setInitialUpdateInterval(60);
    setError(null);
    setJsonError(null);
    setComponentJSON('');
    setActiveTab('natural-language');
    setIsUpdateMode(false);
    setShowFrame(null); // 미지정 상태로 리셋
    setTransparent(null); // 미지정 상태로 리셋
    setLayoutLocked(false); // 폼 리셋 시 위치 고정 옵션 초기화
    setLegendPosition('bottom'); // 범례 위치 초기화
    setShowDescriptionTooltip(true); // description 툴팁 표시 기본값 true로 리셋
    setComponentStyle({}); // 스타일 초기화
    setInitialComponentStyle({}); // 초기 스타일 초기화
    // 초기값들도 리셋
    setInitialShowFrame(null);
    setInitialTransparent(null);
    setInitialLayoutLocked(false);
    // 차트 선택 관련 상태 리셋
    setSelectedChartType(null);
    setChartSelectionMode(false);
  };

  // 자연어 요청 유무 또는 옵션 변경 유무에 따라 제출 버튼 활성화 결정
  const isSubmitButtonEnabled = () => {
    if (loading) return false;
    
    if (activeTab === 'template-editor') {
      return !jsonError && componentJSON.trim();
    }
    
    // 스타일 탭에서는 항상 활성화 (사용자가 스타일 탭에 있다는 것 자체가 변경 의도)
    if (activeTab === 'style-editor') {
      return true;
    }
    
    // 이벤트 탭에서도 항상 활성화 (이벤트 설정을 변경하려는 의도)
    if (activeTab === 'events') {
      return true;
    }
    
    if (activeTab === 'natural-language') {
      // 입력 모드별 활성화 조건
      if (manualMode === 'sql') {
        return canUseSql() && !!sqlText.trim();
      }
      if (manualMode === 'static') {
        return canUseStatic() && !!staticText.trim();
      }
      // LLM
      if (isUpdateMode) return true;
      return selectedChartType || nlRequest.trim();
    }
    
    if (isUpdateMode) {
      return nlRequest.trim() || 
             updateInterval !== initialUpdateInterval ||
             showFrame !== initialShowFrame ||
             transparent !== initialTransparent ||
             layoutLocked !== initialLayoutLocked ||
             isolatedSelection !== initialIsolatedSelection;
    } else {
      // 생성 모드
      return selectedChartType || nlRequest.trim();
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={() => {
        resetForm();
        onClose();
      }}
      size="lg"
      centered
      className="nl-component-modal"
      dialogClassName="modal-90w"
    >
      <Modal.Header>
        <Modal.Title>
          {isUpdateMode ? '컴포넌트 수정하기' : '새 컴포넌트 만들기'}
        </Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={() => {
            resetForm();
            onClose();
          }}
        />
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <Tab.Container id="component-edit-tabs" activeKey={activeTab} onSelect={setActiveTab} className="component-edit-tabs-container">
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="natural-language">{isUpdateMode ? '수정하기' : '차트 만들기'}</Nav.Link>
            </Nav.Item>
            {isUpdateMode && (
              <>
                <Nav.Item>
                  <Nav.Link eventKey="style-editor">스타일/폰트</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="events">이벤트</Nav.Link>
                </Nav.Item>
                {/* 템플릿 직접 편집 탭 - 숨김 처리 */}
                {/* <Nav.Item>
                  <Nav.Link eventKey="template-editor">템플릿 직접 편집</Nav.Link>
                </Nav.Item> */}
              </>
            )}
          </Nav>
          
          <Tab.Content>
            <Tab.Pane eventKey="natural-language" className={chartSelectionMode && !isUpdateMode ? "chart-selection-tab" : ""}>
              {/* 차트 선택 인터페이스는 차트 선택 모드에서만 표시 */}
              
              {/* 차트 선택 인터페이스 - 새 컴포넌트 생성 시 기본으로 표시 */}
              {chartSelectionMode && !isUpdateMode && (
                <div className="chart-selection-wrapper">
                  <div className="chart-selection-header">
                    <h4 className="chart-selection-title">차트 종류를 선택해주세요</h4>
                    <p className="chart-selection-subtitle">원하시는 차트를 클릭하면 바로 생성됩니다</p>
                  </div>
                  <div className="chart-selection-container">
                  {Object.entries(chartCategories).map(([categoryKey, category]) => (
                    <div key={categoryKey} className="chart-category-section">
                      <div className="category-label">{category.label}</div>
                      <div className="chart-grid">
                        {category.charts.map(chartType => {
                          const template = chartTemplates[chartType];
                          if (!template) return null;
                          
                          return (
                              <div
                                key={chartType}
                                className={`chart-card ${selectedChartType === chartType ? 'selected' : ''}`}
                                onClick={() => {
                                  setSelectedChartType(chartType);
                                  const generatedTemplate = generateChartTemplate(chartType);
                                  setCurrentComponent(generatedTemplate);
                                  setComponentJSON(JSON.stringify(generatedTemplate, null, 2));
                                  // 템플릿의 설정값들을 폼에 반영
                                  setUpdateInterval(generatedTemplate.updateInterval || 3600);
                                  setShowFrame(generatedTemplate.chart_options?.showFrame !== undefined ? generatedTemplate.chart_options.showFrame : null);
                                  setTransparent(generatedTemplate.chart_options?.transparent !== undefined ? generatedTemplate.chart_options.transparent : null);
                                  setComponentStyle(generatedTemplate.chart_options?.options?.style || {});
                                  // 차트 선택 완료
                                  setChartSelectionMode(false);
                                }}
                              >
                                <div className="chart-icon">
                                  {template.icon}
                                </div>
                                <div className="chart-name">
                                  {template.name}
                                </div>
                                <div className="chart-desc">
                                  {template.description}
                                </div>
                              </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  
                  </div>
                </div>
              )}
              
              {/* 차트가 선택된 후 설정 화면 */}
              {selectedChartType && !chartSelectionMode && !isUpdateMode && (
                <div className="selected-chart-info">
                  <div className="selected-chart-header">
                    <div className="selected-chart-title">
                      <span className="chart-badge">{getChartIcon(selectedChartType)}</span>
                      <span>{getChartName(selectedChartType)}</span>
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => {
                        setChartSelectionMode(true);
                        setSelectedChartType(null);
                      }}
                      className="change-selection-btn"
                    >
                      다른 차트 선택
                    </Button>
                  </div>
                  <div className="selected-chart-preview">
                    <ChartPreview 
                      chartTemplate={chartTemplates[selectedChartType]?.template} 
                      chartType={selectedChartType}
                    />
                  </div>
                  <div className="preview-info">
                    <small className="text-muted">
                      예시 데이터로 생성된 차트입니다. 생성 후 SQL과 옵션을 수정할 수 있습니다.
                    </small>
                  </div>
                  
                  {/* 웹프레임 차트 선택 시 URL 입력 필드 표시 */}
                  {selectedChartType === 'webframe' && (
                    <div className="webframe-url-section mt-3">
                      <Form.Group>
                        <Form.Label>웹프레임 URL <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={webframeUrl}
                          onChange={(e) => setWebframeUrl(e.target.value)}
                          placeholder="https://example.com 또는 이미지 URL을 입력하세요"
                        />
                        <Form.Text className="text-muted">
                          웹페이지, 이미지 또는 영상 스트림 URL을 입력하세요.
                        </Form.Text>
                      </Form.Group>
                    </div>
                  )}
                </div>
              )}
              
              <Form onSubmit={handleSubmit} style={{ display: chartSelectionMode && !isUpdateMode ? 'none' : 'block' }}>
                {/* 입력 모드 선택: 수동 SQL 모드로 고정 - 숨김 처리 */}
                {/* {isUpdateMode && (
                  <Form.Group className="mb-3">
                    <Form.Label>입력 모드</Form.Label>
                    <div className="d-flex gap-3 align-items-center flex-wrap">
                      <Form.Check
                        type="radio"
                        id="mode-llm"
                        name="inputMode"
                        label="LLM (자연어)"
                        checked={manualMode === 'llm'}
                        onChange={() => setManualMode('llm')}
                        disabled={loading}
                      />
                      {canUseSql() && (
                        <Form.Check
                          type="radio"
                          id="mode-sql"
                          name="inputMode"
                          label="수동 SQL"
                          checked={manualMode === 'sql'}
                          onChange={() => setManualMode('sql')}
                          disabled={loading}
                        />
                      )}
                      {canUseStatic() && (
                        <Form.Check
                          type="radio"
                          id="mode-static"
                          name="inputMode"
                          label="정적 텍스트"
                          checked={manualMode === 'static'}
                          onChange={() => setManualMode('static')}
                          disabled={loading}
                        />
                      )}
                    </div>
                  </Form.Group>
                )} */}
                <Form.Group className="mb-3">
                  <Form.Label>차트 제목</Form.Label>
                  <Form.Control
                    type="text"
                    value={title}
                    onChange={(e) => {
                      if (e.target.value.length <= 50) setTitle(e.target.value);
                    }}
                    placeholder="차트 제목을 입력하세요"
                    maxLength={50}
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    한글, 영문, 숫자, 공백, 특수문자(- _ / ( ) [ ]) 사용 가능 (최대 50자, 현재 {title.length}자)
                  </Form.Text>
                </Form.Group>

                {manualMode === 'llm' && (
                  <Form.Group className="mb-3">
                    <Form.Label>원하는 차트나 데이터를 자연어로 설명해주세요</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={nlRequest}
                      onChange={(e) => setNlRequest(e.target.value)}
                      placeholder={isUpdateMode 
                        ? "예: 이 차트를 지난 3개월간의 SOC 상태를 보여주는 라인 차트로 변경해줘" 
                        : "예: 지난 7일간의 배터리 충전 상태를 보여주는 라인 차트를 생성해줘"}
                      disabled={loading}
                      required={!isUpdateMode}
                    />
                    <Form.Text className="text-muted">
                      {isUpdateMode 
                        ? "구체적으로 설명할수록 더 정확한 컴포넌트가 수정됩니다. 갱신 주기만 변경하려면 이 필드를 비워두세요." 
                        : "구체적으로 설명할수록 더 정확한 컴포넌트가 생성됩니다."}
                    </Form.Text>
                  </Form.Group>
                )}

                {manualMode === 'sql' && canUseSql() && (
                  <Form.Group className="mb-3">
                    <Form.Label>SQL 쿼리</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      value={sqlText}
                      onChange={(e) => setSqlText(e.target.value)}
                      placeholder="SELECT ..."
                      style={{ fontFamily: 'monospace' }}
                      disabled={loading}
                    />
                    {/* 프리뷰 실행 영역 - 숨김 처리 */}
                    {/* <div className="d-flex gap-2 mt-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={loading || !sqlText.trim()}
                        onClick={async () => {
                          setSqlPreview({ loading: true, error: null, columns: [], rows: [] });
                          try {
                            const base = currentComponent || existingComponent || {};
                            const resp = await DashboardService.updateChartData({
                              sql: sqlText,
                              chart_template: base
                            });
                            const cols = resp?.data?.chart_data?.data?.columns || resp?.data?.data?.columns || resp?.data?.columns || [];
                            const rows = resp?.data?.chart_data?.data?.rows || resp?.data?.data?.rows || resp?.data?.rows || [];
                            setSqlPreview({ loading: false, error: null, columns: Array.isArray(cols) ? cols : [], rows: Array.isArray(rows) ? rows : [] });
                          } catch (err) {
                            setSqlPreview({ loading: false, error: err?.message || '프리뷰 실패', columns: [], rows: [] });
                          }
                        }}
                      >
                        프리뷰 실행
                      </Button>
                    </div>
                    <div className="mt-2">
                      {sqlPreview.loading && <small className="text-muted">프리뷰 실행 중...</small>}
                      {!sqlPreview.loading && sqlPreview.error && <small className="text-danger">{sqlPreview.error}</small>}
                      {!sqlPreview.loading && !sqlPreview.error && (sqlPreview.columns.length > 0) && (
                        <Card body className="p-2">
                          <div className="mb-1"><strong>컬럼</strong>: {sqlPreview.columns.join(', ')}</div>
                          <div className="mb-0"><strong>첫 행</strong>: {Array.isArray(sqlPreview.rows) && sqlPreview.rows.length > 0 ? JSON.stringify(sqlPreview.rows[0]) : '없음'}</div>
                        </Card>
                      )}
                    </div> */}
                    <Form.Text className="text-muted d-block mt-1">
                      • SELECT-only 권장, 멀티 스테이트먼트/금칙어(DDL/DML) 사용 금지
                      • 구독 변수는 <code>:variable_name</code> 형태로 바인딩
                    </Form.Text>
                  </Form.Group>
                )}

                {manualMode === 'static' && canUseStatic() && (
                  <Form.Group className="mb-3">
                    <Form.Label>정적 텍스트</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={staticText}
                      onChange={(e) => setStaticText(e.target.value)}
                      placeholder="예: 1,234 kWh 또는 시스템 정상"
                      disabled={loading}
                    />
                    <Form.Text className="text-muted">
                      텍스트 라벨/배지/KPI/프로그레스 등 정적 위젯에서만 사용됩니다.
                    </Form.Text>
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>컴포넌트 설명</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="이 차트의 목적과 제공하는 정보에 대한 간결한 설명을 입력하세요."
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    차트가 표시하는 정보와 이를 통해 파악할 수 있는 인사이트를 설명하세요.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>데이터 갱신 주기 (초)</Form.Label>
                  <Form.Control
                    type="number"
                    min="10"
                    value={updateInterval}
                    onChange={(e) => setUpdateInterval(Number(e.target.value))}
                    disabled={loading}
                  />
                  {isUpdateMode && updateInterval !== initialUpdateInterval && (
                    <Form.Text className="text-info">
                      갱신 주기를 {initialUpdateInterval}초에서 {updateInterval}초로 변경합니다.
                    </Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>차트 프레임 표시</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="show-frame-unset"
                      name="showFrame"
                      label="미지정"
                      checked={showFrame === null}
                      onChange={() => setShowFrame(null)}
                      disabled={loading}
                    />
                    <Form.Check
                      type="radio"
                      id="show-frame-on"
                      name="showFrame"
                      label="표시"
                      checked={showFrame === true}
                      onChange={() => setShowFrame(true)}
                      disabled={loading}
                    />
                    <Form.Check
                      type="radio"
                      id="show-frame-off"
                      name="showFrame"
                      label="숨김"
                      checked={showFrame === false}
                      onChange={() => setShowFrame(false)}
                      disabled={loading}
                    />
                  </div>
                  <Form.Text className="text-muted">
                    차트 주위의 테두리, 그림자, 배경색 표시 여부. 미지정 시 차트 타입에 따라 자동 결정됩니다.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>배경 투명 처리</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="transparent-unset"
                      name="transparent"
                      label="미지정"
                      checked={transparent === null}
                      onChange={() => setTransparent(null)}
                      disabled={loading}
                    />
                    <Form.Check
                      type="radio"
                      id="transparent-on"
                      name="transparent"
                      label="투명"
                      checked={transparent === true}
                      onChange={() => setTransparent(true)}
                      disabled={loading}
                    />
                    <Form.Check
                      type="radio"
                      id="transparent-off"
                      name="transparent"
                      label="불투명"
                      checked={transparent === false}
                      onChange={() => setTransparent(false)}
                      disabled={loading}
                    />
                  </div>
                  <Form.Text className="text-muted">
                    차트 배경 투명도. 미지정 시 차트 타입에 따라 자동 결정됩니다 (text_label은 투명, 나머지는 불투명).
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    모서리 둥글기: {componentStyle?.background?.borderRadius !== undefined ? componentStyle.background.borderRadius : 24}px
                  </Form.Label>
                  <Form.Range
                    min={0}
                    max={120}
                    step={1}
                    value={componentStyle?.background?.borderRadius !== undefined ? componentStyle.background.borderRadius : 24}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setComponentStyle(prev => ({
                        ...prev,
                        background: {
                          ...(prev?.background || {}),
                          borderRadius: value
                        }
                      }));
                    }}
                    disabled={loading}
                  />
                  <div className="d-flex justify-content-between" style={{ fontSize: '11px', color: '#6c757d' }}>
                    <span>0px (각진)</span>
                    <span>120px (둥근)</span>
                  </div>
                </Form.Group>

                {/* 배경색 선택 UX - transparent가 true일 때는 시각적 비활성화 */}
                <Form.Group className="mb-3">
                  <Form.Label>배경색</Form.Label>
                  <div className="d-flex align-items-center gap-3">
                    <Form.Control
                      type="color"
                      value={componentStyle?.background?.color || '#ffffff'}
                      onChange={(e) => handleBackgroundColorChange(e.target.value)}
                      disabled={loading || transparent === true}
                      title="배경색 선택"
                      style={{ width: '48px', height: '36px', padding: 0, border: '1px solid #ced4da', borderRadius: '4px', cursor: transparent === true ? 'not-allowed' : 'pointer' }}
                    />
                    {transparent === true ? (
                      <Form.Text className="text-muted">
                        투명으로 설정된 경우 배경색은 적용되지 않습니다.
                      </Form.Text>
                    ) : (
                      <Form.Text className="text-muted">
                        차트 배경색을 선택합니다.
                      </Form.Text>
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>범례 위치</Form.Label>
                  <Form.Select
                    value={legendPosition}
                    onChange={(e) => {
                      setLegendPosition(e.target.value);
                      // currentComponent 업데이트
                      if (currentComponent) {
                        const updatedComponent = {
                          ...currentComponent,
                          chart_options: {
                            ...currentComponent.chart_options,
                            options: {
                              ...currentComponent.chart_options?.options,
                              advanced: {
                                ...currentComponent.chart_options?.options?.advanced,
                                legendPosition: e.target.value
                              }
                            }
                          }
                        };
                        setCurrentComponent(updatedComponent);
                        setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                      }
                    }}
                    disabled={loading}
                  >
                    <option value="bottom">하단</option>
                    <option value="top">상단</option>
                    <option value="left">왼쪽</option>
                    <option value="right">오른쪽</option>
                    <option value="top-right">차트 내부 (우측 상단)</option>
                    <option value="top-left">차트 내부 (좌측 상단)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    차트 범례의 위치를 설정합니다. '차트 내부' 옵션은 범례를 차트 영역 내부에 배치하여 공간을 절약합니다.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="show-description-tooltip-switch"
                    label="설명 툴팁 표시 (ⓘ)"
                    checked={showDescriptionTooltip}
                    onChange={(e) => setShowDescriptionTooltip(e.target.checked)}
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    차트에 설명(description)이 있을 때 마우스 호버 시 ⓘ 아이콘과 툴팁을 표시합니다.
                  </Form.Text>
                </Form.Group>

                {/* 위치 고정 옵션 - 숨김 처리 */}
                {/* <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="layout-locked-switch"
                    label="위치 고정"
                    checked={layoutLocked}
                    onChange={(e) => setLayoutLocked(e.target.checked)}
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    이 차트의 위치를 고정합니다. 자동 정렬이나 크기 조정 시 이 차트는 현재 위치를 유지하며, 다른 차트들은 이 차트를 피해서 배치됩니다.
                  </Form.Text>
                </Form.Group> */}

                {/* 차트 독립선택 옵션 - 숨김 처리 */}
                {/* <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="isolated-selection-switch"
                    label="차트 독립선택"
                    checked={isolatedSelection}
                    onChange={(e) => setIsolatedSelection(e.target.checked)}
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    런타임 모드에서 다른 차트를 클릭해도 이 차트의 선택 상태를 유지합니다. 이 차트를 다시 클릭하면 선택이 해제됩니다.
                  </Form.Text>
                </Form.Group> */}

                {isUpdateMode && (
                  <div className="update-component-info mb-3">
                    <Alert variant="info">
                      <strong>수정 중인 컴포넌트:</strong> {existingComponent?.title || '이름 없음'}
                      <br />
                      <small>ID: {existingComponent?.id || 'N/A'}</small>
                    </Alert>
                  </div>
                )}
              </Form>
            </Tab.Pane>
            
            {isUpdateMode && (
              <Tab.Pane eventKey="style-editor">
                <div className="style-editor-container" style={{ 
                  maxHeight: '450px', 
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  paddingRight: '15px',
                  paddingBottom: '20px',
                  border: '1px solid #dee2e6',
                  borderRadius: '5px',
                  backgroundColor: '#f8f9fa'
                }}>
                  <div className="style-editor-header mb-3">
                    <h5>스타일 및 폰트 설정</h5>
                    <Form.Text className="text-muted">
                      차트의 제목, 범례, 축 라벨 등의 폰트와 배경 스타일을 설정할 수 있습니다.
                    </Form.Text>
                  </div>
                  
                  {/* 웹프레임 차트인 경우 URL 설정 표시 */}
                  {existingComponent?.chart_options?.viz_type === 'webframe' && (
                    <div className="webframe-config-section">
                      {/* 1. 기본 URL */}
                      <div className="webframe-url-section mb-4" style={{
                        padding: '15px',
                        backgroundColor: '#ffffff',
                        borderRadius: '5px',
                        border: '1px solid #dee2e6'
                      }}>
                        <Form.Group>
                          <Form.Label>
                            <strong>웹프레임 URL</strong> <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={webframeUrl}
                            onChange={(e) => setWebframeUrl(e.target.value)}
                            placeholder="https://example.com/path/{:variable}"
                          />
                          <Form.Text className="text-muted">
                            웹페이지, 이미지 또는 영상 스트림 URL을 입력하세요.<br/>
                            💡 URL에 <code>{'{:변수명}'}</code> 형식으로 변수를 삽입할 수 있습니다.<br/>
                            &nbsp;&nbsp;• 변수만: <code>/golfcart/{'{:cartId}'}</code><br/>
                            &nbsp;&nbsp;• 상수+변수: <code>/golfcart/cart_{'{:number}'}/view</code>
                          </Form.Text>
                        </Form.Group>
                      </div>

                      {/* 2. SSO 토큰 설정 - 숨김 처리 */}
                      {/* <div className="sso-config-section mb-4" style={{
                        padding: '15px',
                        backgroundColor: '#ffffff',
                        borderRadius: '5px',
                        border: '1px solid #dee2e6'
                      }}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <Form.Check
                            type="checkbox"
                            label={<strong>SSO 토큰 전달 활성화</strong>}
                            checked={ssoConfig.enabled}
                            onChange={(e) => setSsoConfig(prev => ({
                              ...prev,
                              enabled: e.target.checked
                            }))}
                          />
                          <Badge
                            bg="info"
                            pill
                            style={{
                              cursor: 'pointer',
                              fontSize: '12px',
                              padding: '4px 8px'
                            }}
                            title="연동 정보 보기"
                            onClick={(e) => {
                              e.preventDefault();
                              const infoText = `
📋 SSO 연동 기술 정보

• JWT 알고리즘: HS256
• SECRET_KEY: 시스템 관리자에게 요청
  (보안상 UI에 표시하지 않습니다)

• JWT Payload 구조:
  {
    "user_id": 123,
    "username": "user",
    "email": "user@example.com",
    "tenant_id": "ess",
    "is_staff": false,
    "exp": 1234567890,  // 만료 시간
    "iat": 1234567890,  // 발급 시간
    "token_type": "access",
    "jti": "unique-id"
  }

• 외부 앱 검증 예시 (Node.js):
  const jwt = require('jsonwebtoken');
  const SECRET_KEY = process.env.SECRET_KEY;

  jwt.verify(token, SECRET_KEY, {
    algorithms: ['HS256']
  });

• 외부 앱 검증 예시 (Python):
  import jwt
  SECRET_KEY = os.environ.get('SECRET_KEY')

  decoded = jwt.decode(
    token,
    SECRET_KEY,
    algorithms=['HS256']
  )

📖 상세 문서:
   doc/tech_standard_doc/webframe_sso_standard.md
                              `.trim();

                              alert(infoText);
                            }}
                          >
                            ⓘ 연동 정보
                          </Badge>
                        </div>

                        {ssoConfig.enabled && (
                          <>
                            <Form.Group className="mb-3">
                              <Form.Label>전달 방식</Form.Label>
                              <div>
                                <Form.Check
                                  inline
                                  type="radio"
                                  label="쿼리 파라미터 (?token=...)"
                                  name="ssoMethod"
                                  checked={ssoConfig.method === 'query'}
                                  onChange={() => setSsoConfig(prev => ({
                                    ...prev,
                                    method: 'query'
                                  }))}
                                />
                                <Form.Check
                                  inline
                                  type="radio"
                                  label="해시 프래그먼트 (#token=...)"
                                  name="ssoMethod"
                                  checked={ssoConfig.method === 'hash'}
                                  onChange={() => setSsoConfig(prev => ({
                                    ...prev,
                                    method: 'hash'
                                  }))}
                                />
                              </div>
                              <Form.Text className="text-muted">
                                💡 <strong>권장:</strong> 해시 방식은 브라우저 히스토리에 남지 않아 더 안전합니다.
                              </Form.Text>
                            </Form.Group>

                            <Form.Group>
                              <Form.Label>토큰 파라미터 이름</Form.Label>
                              <Form.Control
                                type="text"
                                value={ssoConfig.param_name}
                                onChange={(e) => setSsoConfig(prev => ({
                                  ...prev,
                                  param_name: e.target.value
                                }))}
                                placeholder="auth_token"
                              />
                              <Form.Text className="text-muted">
                                iframe 앱에서 인식할 토큰 파라미터명을 입력하세요.
                              </Form.Text>
                            </Form.Group>
                          </>
                        )}
                      </div> */}

                      {/* 3. 쿼리 파라미터 매핑 - 숨김 처리 */}
                      {/* <div className="query-params-section mb-4" style={{
                        padding: '15px',
                        backgroundColor: '#ffffff',
                        borderRadius: '5px',
                        border: '1px solid #dee2e6'
                      }}>
                        <h6 className="mb-3">쿼리 파라미터 매핑</h6>

                        {queryParams.map((param, index) => (
                          <div key={index} className="d-flex gap-2 mb-2 align-items-start">
                            <Form.Control
                              type="text"
                              placeholder="파라미터명 (예: battery_id)"
                              value={param.param_name || ''}
                              onChange={(e) => {
                                const newParams = [...queryParams];
                                newParams[index] = { ...newParams[index], param_name: e.target.value };
                                setQueryParams(newParams);
                              }}
                              style={{ flex: '1' }}
                            />

                            <div style={{ flex: '1.5', position: 'relative' }}>
                              <Form.Control
                                type="text"
                                placeholder="값 템플릿 (예: golfcart_{:number})"
                                value={param.variable_name || ''}
                                onChange={(e) => {
                                  const newParams = [...queryParams];
                                  newParams[index] = { ...newParams[index], variable_name: e.target.value };
                                  setQueryParams(newParams);
                                  setAutoCompleteQuery(prev => ({ ...prev, [index]: e.target.value }));
                                }}
                                onFocus={() => {
                                  setShowAutoComplete(index);
                                  setAutoCompleteQuery(prev => ({ ...prev, [index]: '' }));
                                }}
                                onBlur={() => setTimeout(() => setShowAutoComplete(null), 200)}
                              />

                              {showAutoComplete === index && (
                                <div style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                  backgroundColor: 'white',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '4px',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  zIndex: 1000,
                                  marginTop: '2px'
                                }}>
                                  {availablePublishVars
                                    .filter(v => {
                                      const query = (autoCompleteQuery[index] || '').replace(/^\{?:/, '').replace(/\}?$/, '');
                                      return query.length === 0 ? true : v.toLowerCase().includes(query.toLowerCase());
                                    })
                                    .map(varName => (
                                      <div
                                        key={varName}
                                        style={{
                                          padding: '8px 12px',
                                          cursor: 'pointer',
                                          borderBottom: '1px solid #f0f0f0'
                                        }}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          const newParams = [...queryParams];
                                          const currentValue = newParams[index].variable_name || '';
                                          const insertValue = currentValue.includes('{:') ? varName : `{:${varName}}`;
                                          newParams[index] = { ...newParams[index], variable_name: insertValue };
                                          setQueryParams(newParams);
                                          setShowAutoComplete(null);
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'white';
                                        }}
                                      >
                                        {'{:' + varName + '}'}
                                      </div>
                                    ))}
                                  {availablePublishVars.filter(v => {
                                    const query = (autoCompleteQuery[index] || '').replace(/^\{?:/, '').replace(/\}?$/, '');
                                    return query.length === 0 ? true : v.toLowerCase().includes(query.toLowerCase());
                                  }).length === 0 && (
                                    <div style={{
                                      padding: '8px 12px',
                                      color: '#6c757d',
                                      fontStyle: 'italic'
                                    }}>
                                      템플릿: {param.variable_name || '(입력하세요)'}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            <Form.Control
                              type="text"
                              placeholder="기본값 (선택)"
                              value={param.default_value || ''}
                              onChange={(e) => {
                                const newParams = [...queryParams];
                                newParams[index] = { ...newParams[index], default_value: e.target.value };
                                setQueryParams(newParams);
                              }}
                              style={{ flex: '1' }}
                            />
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                const newParams = queryParams.filter((_, i) => i !== index);
                                setQueryParams(newParams);
                              }}
                            >
                              ✕
                            </Button>
                          </div>
                        ))}

                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setQueryParams([...queryParams, {
                              param_name: '',
                              variable_name: '',
                              default_value: ''
                            }]);
                          }}
                          className="mt-2"
                        >
                          + 파라미터 추가
                        </Button>

                        <Form.Text className="text-muted d-block mt-2">
                          💡 값 템플릿 사용법:<br/>
                          &nbsp;&nbsp;• <code>{'{:변수명}'}</code> → 동적 변수 (예: <code>{'{:selectedBattery}'}</code>)<br/>
                          &nbsp;&nbsp;• <code>텍스트{'{:변수}'}텍스트</code> → 조합 (예: <code>golfcart_{'{:number}'}</code>)<br/>
                          &nbsp;&nbsp;• <code>텍스트</code> → 고정 상수 (예: <code>golfcart</code>)<br/>
                          &nbsp;&nbsp;• 클릭하면 사용 가능한 변수 목록이 표시됩니다.
                        </Form.Text>
                      </div> */}

                      {/* 4. URL 미리보기 */}
                      <div className="url-preview-section mb-4" style={{
                        padding: '15px',
                        backgroundColor: '#ffffff',
                        borderRadius: '5px',
                        border: '1px solid #dee2e6'
                      }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">🔍 URL 미리보기</h6>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={async () => {
                              if (!webframeUrl) {
                                alert('URL을 먼저 입력해주세요.');
                                return;
                              }

                              try {
                                // 템플릿 치환 함수 (실제 렌더링 로직과 동일하게)
                                const replaceVariables = (template) => {
                                  if (!template) return '';
                                  return template.replace(/\{:(\w+)\}/g, (match, varName) => {
                                    const value = globalVars?.[varName];
                                    // 실제 렌더링과 동일: 변수 없으면 빈 문자열
                                    return value !== undefined && value !== null ? String(value) : '';
                                  });
                                };

                                const breakdown = {
                                  originalUrl: webframeUrl,
                                  pathReplaced: '',
                                  queryParams: [],
                                  ssoToken: null,
                                  finalUrl: ''
                                };

                                // 1. Path 변수 치환
                                breakdown.pathReplaced = replaceVariables(webframeUrl);
                                const url = new URL(breakdown.pathReplaced);

                                // 2. 쿼리 파라미터 추가
                                if (queryParams && queryParams.length > 0) {
                                  queryParams.forEach(({ param_name, variable_name, default_value }) => {
                                    if (!param_name) return;
                                    
                                    let value = replaceVariables(variable_name || '');
                                    if (!value && default_value) {
                                      value = default_value;
                                    }
                                    
                                    if (value !== '') {
                                      url.searchParams.set(param_name, value);
                                      breakdown.queryParams.push({
                                        name: param_name,
                                        template: variable_name,
                                        value: value
                                      });
                                    }
                                  });
                                }

                                // 3. SSO 토큰 추가
                                if (ssoConfig?.enabled) {
                                  try {
                                    const response = await axios.get('/api/users/current-token/', {
                                      withCredentials: true
                                    });
                                    const token = response.data.token;
                                    breakdown.ssoToken = {
                                      method: ssoConfig.method,
                                      paramName: ssoConfig.param_name || 'auth_token',
                                      value: token
                                    };

                                    if (ssoConfig.method === 'hash') {
                                      url.hash = `${ssoConfig.param_name || 'auth_token'}=${token}`;
                                    } else {
                                      url.searchParams.set(ssoConfig.param_name || 'auth_token', token);
                                    }
                                  } catch (error) {
                                    breakdown.ssoToken = {
                                      method: ssoConfig.method,
                                      paramName: ssoConfig.param_name || 'auth_token',
                                      error: '토큰 조회 실패'
                                    };
                                  }
                                }

                                breakdown.finalUrl = url.toString();
                                setUrlPreview(breakdown.finalUrl);
                                setUrlPreviewBreakdown(breakdown);
                                setShowUrlPreview(true);
                              } catch (error) {
                                alert(`URL 생성 실패: ${error.message}`);
                              }
                            }}
                          >
                            미리보기 생성
                          </Button>
                        </div>

                        {showUrlPreview && urlPreview && (
                          <div style={{ marginTop: '15px' }}>
                            {/* 최종 URL */}
                            <div style={{
                              padding: '10px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              marginBottom: '10px',
                              wordBreak: 'break-all'
                            }}>
                              <small className="text-muted d-block mb-1"><strong>최종 URL:</strong></small>
                              <code style={{ fontSize: '12px' }}>{urlPreview}</code>
                            </div>

                            {/* 분해된 정보 */}
                            {urlPreviewBreakdown && (
                              <details style={{ marginTop: '10px' }}>
                                <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
                                  📋 URL 구성 요소 보기
                                </summary>
                                
                                <div style={{ paddingLeft: '15px' }}>
                                  {/* 원본 URL */}
                                  <div className="mb-2">
                                    <small className="text-muted"><strong>1. 원본 URL:</strong></small>
                                    <div><code style={{ fontSize: '11px' }}>{urlPreviewBreakdown.originalUrl}</code></div>
                                  </div>

                                  {/* Path 치환 결과 */}
                                  {urlPreviewBreakdown.originalUrl !== urlPreviewBreakdown.pathReplaced && (
                                    <div className="mb-2">
                                      <small className="text-muted"><strong>2. Path 변수 치환 후:</strong></small>
                                      <div><code style={{ fontSize: '11px' }}>{urlPreviewBreakdown.pathReplaced}</code></div>
                                    </div>
                                  )}

                                  {/* 쿼리 파라미터 */}
                                  {urlPreviewBreakdown.queryParams.length > 0 && (
                                    <div className="mb-2">
                                      <small className="text-muted"><strong>3. 쿼리 파라미터:</strong></small>
                                      {urlPreviewBreakdown.queryParams.map((qp, idx) => (
                                        <div key={idx} style={{ paddingLeft: '10px', fontSize: '11px' }}>
                                          • <code>{qp.name}</code> = <code>{qp.value}</code>
                                          {qp.template && <span className="text-muted"> (템플릿: {qp.template})</span>}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* SSO 토큰 */}
                                  {urlPreviewBreakdown.ssoToken && (
                                    <div className="mb-2">
                                      <small className="text-muted"><strong>4. SSO 토큰:</strong></small>
                                      <div style={{ paddingLeft: '10px', fontSize: '11px' }}>
                                        • 전달 방식: <Badge bg={urlPreviewBreakdown.ssoToken.method === 'hash' ? 'success' : 'info'}>
                                          {urlPreviewBreakdown.ssoToken.method === 'hash' ? '해시 (#)' : '쿼리 (?)'}
                                        </Badge>
                                        <br/>
                                        • 파라미터명: <code>{urlPreviewBreakdown.ssoToken.paramName}</code>
                                        <br/>
                                        {urlPreviewBreakdown.ssoToken.error ? (
                                          <>• 값: <span className="text-danger">{urlPreviewBreakdown.ssoToken.error}</span></>
                                        ) : (
                                          <>• 값: <code style={{ wordBreak: 'break-all' }}>{urlPreviewBreakdown.ssoToken.value.substring(0, 50)}...</code></>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </details>
                            )}
                          </div>
                        )}

                        {!showUrlPreview && (
                          <Form.Text className="text-muted d-block mt-2">
                            현재 설정으로 생성될 최종 URL을 미리 볼 수 있습니다.
                          </Form.Text>
                        )}
                      </div>
                    </div>
                  )}

              {/* 텍스트 라벨 전용 데이터 소스 섹션 (static/sql)
                  수동 모드/정적 모드를 자연어 탭에서 통합 제공하기 때문에 중복 방지 위해 숨김 */}
              {false && (currentComponent?.chart_options?.viz_type === 'text_label' || existingComponent?.chart_options?.viz_type === 'text_label') && (
                <div className="textlabel-datasource-section mb-4" style={{
                  padding: '15px',
                  backgroundColor: '#ffffff',
                  borderRadius: '5px',
                  border: '1px solid #dee2e6'
                }}>
                  <h6 className="mb-3">데이터 소스</h6>
                  {(() => {
                    const dsType = (currentComponent?.data_source?.type || existingComponent?.data_source?.type || 'static');
                    const handleSwitchMode = (nextType) => {
                      if (!currentComponent && !existingComponent) return;
                      const base = currentComponent || existingComponent;
                      const updated = {
                        ...base,
                        data_source: {
                          ...(base.data_source || {}),
                          type: nextType
                        },
                        chart_options: {
                          ...(base.chart_options || {}),
                          options: {
                            ...((base.chart_options && base.chart_options.options) || {})
                          }
                        }
                      };
                      // 모드 전환 시 불필요 필드 정리
                      if (nextType === 'static') {
                        // query 제거, content 보존 또는 초기화
                        if (updated.data_source && 'query' in updated.data_source) delete updated.data_source.query;
                        const content = (base.chart_options?.options?.content) || (base.data_source?.content) || '';
                        updated.data_source.content = content;
                        updated.chart_options.options.content = content;
                      } else {
                        // static 필드 정리
                        if (updated.data_source && 'content' in updated.data_source) delete updated.data_source.content;
                      }
                      setCurrentComponent(updated);
                      try { setComponentJSON(JSON.stringify(updated, null, 2)); } catch (_) {}
                    };

                    const handleStaticContentChange = (value) => {
                      if (!currentComponent && !existingComponent) return;
                      const base = currentComponent || existingComponent;
                      const updated = {
                        ...base,
                        data_source: {
                          ...(base.data_source || {}),
                          type: 'static',
                          content: value
                        },
                        chart_options: {
                          ...(base.chart_options || {}),
                          options: {
                            ...((base.chart_options && base.chart_options.options) || {}),
                            content: value
                          }
                        }
                      };
                      setCurrentComponent(updated);
                      try { setComponentJSON(JSON.stringify(updated, null, 2)); } catch (_) {}
                    };

                    const handleSqlQueryChange = (value) => {
                      if (!currentComponent && !existingComponent) return;
                      const base = currentComponent || existingComponent;
                      const updated = {
                        ...base,
                        data_source: {
                          ...(base.data_source || {}),
                          type: 'sql',
                          query: value
                        },
                        chart_options: {
                          ...(base.chart_options || {}),
                          options: {
                            ...((base.chart_options && base.chart_options.options) || {})
                          }
                        }
                      };
                      // static content는 남겨두되, 렌더 혼선 방지 위해 options.content는 유지하되 SQL 모드에서는 서버 결과 우선
                      setCurrentComponent(updated);
                      try { setComponentJSON(JSON.stringify(updated, null, 2)); } catch (_) {}
                    };

                    const staticContentValue = (currentComponent?.data_source?.type === 'static' && (currentComponent?.data_source?.content ?? currentComponent?.chart_options?.options?.content))
                      || (existingComponent?.data_source?.type === 'static' && (existingComponent?.data_source?.content ?? existingComponent?.chart_options?.options?.content))
                      || '';
                    const sqlQueryValue = (currentComponent?.data_source?.type === 'sql' && (currentComponent?.data_source?.query || ''))
                      || (existingComponent?.data_source?.type === 'sql' && (existingComponent?.data_source?.query || ''))
                      || '';

                    return (
                      <>
                        <Form.Group className="mb-2">
                          <div className="d-flex gap-4 align-items-center">
                            <Form.Check
                              type="radio"
                              id="textlabel-mode-static"
                              name="textlabel-ds-mode"
                              label="Static"
                              checked={dsType === 'static'}
                              onChange={() => handleSwitchMode('static')}
                            />
                            <Form.Check
                              type="radio"
                              id="textlabel-mode-sql"
                              name="textlabel-ds-mode"
                              label="SQL"
                              checked={dsType === 'sql'}
                              onChange={() => handleSwitchMode('sql')}
                            />
                          </div>
                          <Form.Text className="text-muted">
                            텍스트 라벨은 Static/SQL 모두 지원합니다. Static은 직접 텍스트를 지정하고, SQL은 첫 행 첫 컬럼 값을 표시합니다.
                          </Form.Text>
                        </Form.Group>

                        {dsType === 'static' && (
                          <Form.Group className="mt-2">
                            <Form.Label>표시 텍스트</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={staticContentValue}
                              onChange={(e) => handleStaticContentChange(e.target.value)}
                              placeholder="예: 1,234 kWh 또는 시스템 정상"
                            />
                            <Form.Text className="text-muted">
                              값은 `chart_options.options.content`와 `data_source.content`에 동기화됩니다.
                            </Form.Text>
                          </Form.Group>
                        )}

                        {dsType === 'sql' && (
                          <Form.Group className="mt-2">
                            <Form.Label>SQL 쿼리</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={5}
                              value={sqlQueryValue}
                              onChange={(e) => handleSqlQueryChange(e.target.value)}
                              placeholder="SELECT value FROM your_table LIMIT 1"
                              style={{ fontFamily: 'monospace' }}
                            />
                            <Form.Text className="text-muted">
                              첫 번째 행의 첫 번째 값을 텍스트로 표시합니다. 안전을 위해 파라미터 바인딩 규칙을 따르세요.
                            </Form.Text>
                          </Form.Group>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
                  
                  {/* 웹프레임 차트가 아닐 때만 스타일 편집 패널 표시 */}
                  {existingComponent?.chart_options?.viz_type !== 'webframe' && (
                    <CommonStylePanel
                      chartType={(currentComponent?.viz_type || currentComponent?.chart_options?.viz_type || existingComponent?.viz_type || existingComponent?.chart_options?.viz_type || 'bar').toLowerCase()}
                      style={styleForCommonPanel}
                      axes={currentComponent?.chart_options?.options?.axes || existingComponent?.chart_options?.options?.axes || {}}
                      onAxesChange={(newAxes) => {
                        if (currentComponent) {
                          // 축 제목 텍스트 입력 시 해당 축의 visibility도 자동으로 켜기
                          const currentStyle = currentComponent.chart_options?.options?.style || {};
                          const currentVisibility = currentStyle.visibility || {};
                          const axisVisibility = { ...(currentVisibility.axis || {}) };
                          if (newAxes.x) {
                            if (!axisVisibility.x) axisVisibility.x = {};
                            axisVisibility.x = { ...axisVisibility.x, title: true };
                          }
                          if (newAxes.y) {
                            if (!axisVisibility.y) axisVisibility.y = {};
                            axisVisibility.y = { ...axisVisibility.y, title: true };
                          }
                          const updatedStyle = {
                            ...currentStyle,
                            visibility: { ...currentVisibility, axis: axisVisibility }
                          };
                          const updatedComponent = {
                            ...currentComponent,
                            chart_options: {
                              ...currentComponent.chart_options,
                              options: {
                                ...currentComponent.chart_options?.options,
                                axes: newAxes,
                                style: updatedStyle
                              }
                            }
                          };
                          // componentStyle도 동기화 (저장 시 반영되도록)
                          setComponentStyle(updatedStyle);
                          setCurrentComponent(updatedComponent);
                          setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                        }
                      }}
                    onChange={(newStyle) => {
                      // advanced를 제외한 스타일만 componentStyle에 저장
                      const { advanced: styleAdvanced, ...cleanStyle } = newStyle;
                      setComponentStyle(cleanStyle);
                      // currentComponent 업데이트
                      if (currentComponent) {
                        // visibility를 별도로 처리
                        const { visibility, ...styleWithoutSpecial } = cleanStyle;
                        
                        const updatedComponent = {
                          ...currentComponent,
                          chart_options: {
                            ...currentComponent.chart_options,
                            options: {
                              ...currentComponent.chart_options?.options,
                              style: {
                                ...styleWithoutSpecial,
                                // visibility는 style 아래에 위치
                                ...(visibility && { visibility })
                              },
                              // advanced는 options 아래에 위치 (tableOptions 포함)
                              ...(styleAdvanced && { advanced: {
                                ...currentComponent.chart_options?.options?.advanced,
                                ...styleAdvanced
                              }})
                            }
                          }
                        };
                        
                        // showFrame과 transparent 상태 반영
                        if (showFrame === null) {
                          delete updatedComponent.chart_options.showFrame;
                        } else {
                          updatedComponent.chart_options.showFrame = showFrame;
                        }
                        
                        if (transparent === null) {
                          delete updatedComponent.chart_options.transparent;
                        } else {
                          updatedComponent.chart_options.transparent = transparent;
                        }
                        
                        setCurrentComponent(updatedComponent); // 현재 컴포넌트 상태 업데이트
                        setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                      }
                    }}
                    showPreview={true}
                  />
                  )}

                  {/* 테이블 전용: 간단한 컬럼 너비(percent) 편집 섹션 */}
                  {(() => {
                    const viz = (currentComponent?.chart_options?.viz_type || existingComponent?.chart_options?.viz_type || currentComponent?.viz_type || existingComponent?.viz_type || '').toLowerCase();
                    if (viz !== 'table') return null;

                    const columns = eventTableColumns || [];
                    const widthSpec = currentComponent?.chart_options?.options?.style?.table?.columnWidths
                      || existingComponent?.chart_options?.options?.style?.table?.columnWidths
                      || { mode: 'auto', columns: [], unspecified: 'auto' };

                    if (tableWidthsRef.current == null) {
                      tableWidthsRef.current = {
                        mode: widthSpec.mode || 'auto',
                        unspecified: widthSpec.unspecified || 'auto',
                        rows: Array.isArray(widthSpec.columns) ? widthSpec.columns.map(c => ({ key: c.key || '', percent: c.percent ?? '' })) : []
                      };
                    }

                    const setAndSync = (next) => {
                      // currentComponent에 저장
                      if (!currentComponent) return;
                      const base = currentComponent;
                      const nextSpec = (next.mode === 'percent' && next.rows.length > 0)
                        ? { mode: 'percent', unspecified: next.unspecified, columns: next.rows.filter(r => r.key).map(r => ({ key: r.key, percent: Number(r.percent) || 0 })) }
                        : { mode: 'auto' };
                      const updated = {
                        ...base,
                        chart_options: {
                          ...base.chart_options,
                          options: {
                            ...base.chart_options?.options,
                            style: {
                              ...base.chart_options?.options?.style,
                              table: {
                                ...base.chart_options?.options?.style?.table,
                                columnWidths: nextSpec
                              }
                            }
                          }
                        }
                      };
                      tableWidthsRef.current = next;
                      setCurrentComponent(updated);
                      try { setComponentJSON(JSON.stringify(updated, null, 2)); } catch (_) {}
                    };

                    const sumPercent = () => (tableWidthsRef.current.rows || []).reduce((acc, r) => acc + (Number(r.percent) || 0), 0);

                    const normalize100 = () => {
                      const rows = tableWidthsRef.current.rows || [];
                      const total = rows.reduce((acc, r) => acc + (Number(r.percent) || 0), 0);
                      if (total <= 0) return;
                      const scaled = rows.map(r => ({ ...r, percent: Math.round(((Number(r.percent) || 0) * 10000) / total) / 100 }));
                      setAndSync({ ...tableWidthsRef.current, rows: scaled });
                    };

                    const addRow = () => {
                      setAndSync({ ...tableWidthsRef.current, mode: 'percent', rows: [...(tableWidthsRef.current.rows || []), { key: '', percent: '' }] });
                    };
                    const removeRow = (idx) => {
                      const rows = [...(tableWidthsRef.current.rows || [])];
                      rows.splice(idx, 1);
                      const nextMode = rows.length === 0 ? 'auto' : tableWidthsRef.current.mode;
                      setAndSync({ ...tableWidthsRef.current, mode: nextMode, rows });
                    };

                    const setRow = (idx, field, value) => {
                      const rows = [...(tableWidthsRef.current.rows || [])];
                      const row = { ...(rows[idx] || {}) };
                      row[field] = value;
                      rows[idx] = row;
                      setAndSync({ ...tableWidthsRef.current, rows });
                    };

                    const setMode = (mode) => setAndSync({ ...tableWidthsRef.current, mode });
                    const setUnspecified = (unspecified) => setAndSync({ ...tableWidthsRef.current, unspecified });

                    return (
                      <div className="table-column-widths-section mt-4" style={{ padding: '15px', backgroundColor: '#ffffff', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                        <h6 className="mb-2">테이블 컬럼 너비(%)</h6>
                        <div className="d-flex gap-3 align-items-center mb-2">
                          <Form.Check
                            type="radio"
                            id="table-width-mode-auto"
                            name="table-width-mode"
                            label="자동설정"
                            checked={tableWidthsRef.current.mode !== 'percent'}
                            onChange={() => setMode('auto')}
                          />
                          <Form.Check
                            type="radio"
                            id="table-width-mode-percent"
                            name="table-width-mode"
                            label="수동설정(percent)"
                            checked={tableWidthsRef.current.mode === 'percent'}
                            onChange={() => setMode('percent')}
                          />
                          <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#666' }}>
                            합계: {sumPercent().toFixed(2)}%
                          </div>
                          <Button size="sm" variant="outline-secondary" onClick={normalize100}>100% 정규화</Button>
                        </div>

                        {tableWidthsRef.current.mode === 'percent' && (
                          <>
                            <div className="d-flex gap-2 align-items-center mb-2">
                              <Form.Label className="mb-0" style={{ width: '140px' }}>미지정 컬럼</Form.Label>
                              <Form.Select size="sm" style={{ maxWidth: '160px' }} value={tableWidthsRef.current.unspecified} onChange={(e) => setUnspecified(e.target.value)}>
                                <option value="auto">자동폭(auto)</option>
                                <option value="equal">남은 퍼센트 균등 분배(equal)</option>
                              </Form.Select>
                            </div>
                            <div style={{ borderTop: '1px dashed #e0e0e0', margin: '8px 0' }} />
                            {(tableWidthsRef.current.rows || []).map((r, idx) => (
                              <div key={idx} className="d-flex gap-2 align-items-center mb-2">
                                <Form.Select size="sm" style={{ maxWidth: '240px' }} value={r.key} onChange={(e) => setRow(idx, 'key', e.target.value)}>
                                  <option value="">컬럼 선택...</option>
                                  {columns.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </Form.Select>
                                <Form.Control size="sm" type="number" placeholder="%" value={r.percent} onChange={(e) => setRow(idx, 'percent', e.target.value)} style={{ width: '100px' }} />
                                <Button size="sm" variant="outline-danger" onClick={() => removeRow(idx)}>삭제</Button>
                              </div>
                            ))}
                            <Button size="sm" variant="outline-primary" onClick={addRow}>규칙 추가</Button>
                            {sumPercent().toFixed(2) !== '100.00' && (
                              <div style={{ marginTop: '6px', fontSize: '12px', color: '#d9534f' }}>합계가 100%가 아닙니다. 정규화를 권장합니다.</div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* 프로그레스바 차트인 경우 고유 옵션 표시 */}
                  {(currentComponent?.chart_options?.viz_type === 'progress_bar' || existingComponent?.chart_options?.viz_type === 'progress_bar') && (
                    <div className="progress-bar-options-section mt-4" style={{
                      padding: '15px',
                      backgroundColor: '#ffffff',
                      borderRadius: '5px',
                      border: '1px solid #dee2e6'
                    }}>
                      <h6 className="mb-3">프로그레스바 고유 옵션</h6>

                      {/* 비교값 표시 토글 및 관련 설정 */}
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          id="progress-show-comparison"
                          label="비교값 표시"
                          checked={currentComponent?.chart_options?.options?.advanced?.showComparison || false}
                          onChange={(e) => {
                            if (!currentComponent) return;
                            const updatedComponent = {
                              ...currentComponent,
                              chart_options: {
                                ...currentComponent.chart_options,
                                options: {
                                  ...currentComponent.chart_options?.options,
                                  advanced: {
                                    ...currentComponent.chart_options?.options?.advanced,
                                    showComparison: e.target.checked
                                  }
                                }
                              }
                            };
                            if (!e.target.checked) {
                              try {
                                delete updatedComponent.chart_options.options.advanced.comparisonValue;
                                delete updatedComponent.chart_options.options.advanced.comparisonLabel;
                                delete updatedComponent.chart_options.options.advanced.comparisonColor;
                                delete updatedComponent.chart_options.options.advanced.comparisonLineWidth;
                                delete updatedComponent.chart_options.options.advanced.comparisonLineStyle;
                              } catch (_) {}
                            }
                            setCurrentComponent(updatedComponent);
                            setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                          }}
                        />
                        <Form.Text className="text-muted">
                          목표값이나 평균값 등의 비교 기준을 프로그레스바에 표시합니다.
                        </Form.Text>
                      </Form.Group>

                      {currentComponent?.chart_options?.options?.advanced?.showComparison && (
                        <>
                          {/* 비교값 입력 방식 및 입력 UI */}
                          <Form.Group className="mb-2">
                            <Form.Label>비교값 입력 방식</Form.Label>
                            <div className="d-flex gap-3">
                              <Form.Check
                                type="radio"
                                id="pb-comparison-const"
                                name="pb-comparison-mode"
                                label="상수"
                                checked={comparisonInputMode === 'constant'}
                                onChange={() => {
                                  setComparisonInputMode('constant');
                                  if (currentComponent) {
                                    const updated = {
                                      ...currentComponent,
                                      chart_options: {
                                        ...currentComponent.chart_options,
                                        options: {
                                          ...currentComponent.chart_options?.options,
                                          advanced: {
                                            ...currentComponent.chart_options?.options?.advanced,
                                            comparisonValue: comparisonConst
                                          }
                                        }
                                      }
                                    };
                                    setCurrentComponent(updated);
                                    setComponentJSON(JSON.stringify(updated, null, 2));
                                  }
                                }}
                              />
                              <Form.Check
                                type="radio"
                                id="pb-comparison-var"
                                name="pb-comparison-mode"
                                label="변수"
                                checked={comparisonInputMode === 'variable'}
                                onChange={() => {
                                  setComparisonInputMode('variable');
                                  if (currentComponent) {
                                    const updated = {
                                      ...currentComponent,
                                      chart_options: {
                                        ...currentComponent.chart_options,
                                        options: {
                                          ...currentComponent.chart_options?.options,
                                          advanced: {
                                            ...currentComponent.chart_options?.options?.advanced,
                                            comparisonValue: comparisonVar ? `:${comparisonVar}` : ''
                                          }
                                        }
                                      }
                                    };
                                    setCurrentComponent(updated);
                                    setComponentJSON(JSON.stringify(updated, null, 2));
                                  }
                                }}
                              />
                            </div>
                          </Form.Group>

                          {comparisonInputMode === 'constant' && (
                            <Form.Group className="mb-3">
                              <Form.Label>비교값 (상수)</Form.Label>
                              <Form.Control
                                type="number"
                                value={comparisonConst}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setComparisonConst(v);
                                  if (!currentComponent) return;
                                  const updated = {
                                    ...currentComponent,
                                    chart_options: {
                                      ...currentComponent.chart_options,
                                      options: {
                                        ...currentComponent.chart_options?.options,
                                        advanced: {
                                          ...currentComponent.chart_options?.options?.advanced,
                                          comparisonValue: v
                                        }
                                      }
                                    }
                                  };
                                  setCurrentComponent(updated);
                                  setComponentJSON(JSON.stringify(updated, null, 2));
                                }}
                                placeholder="예: 75"
                              />
                            </Form.Group>
                          )}

                          {comparisonInputMode === 'variable' && (
                            <Form.Group className="mb-3">
                              <Form.Label>비교값 (변수 선택 또는 직접 입력)</Form.Label>
                              {/* 입력 + datalist로 선택/입력 모두 지원 */}
                              <Form.Control
                                type="text"
                                list="pb-comparison-var-options"
                                value={comparisonVar}
                                placeholder="예: selected_soc"
                                onChange={(e) => {
                                  const name = e.target.value.trim();
                                  setComparisonVar(name);
                                  if (!currentComponent) return;
                                  const updated = {
                                    ...currentComponent,
                                    chart_options: {
                                      ...currentComponent.chart_options,
                                      options: {
                                        ...currentComponent.chart_options?.options,
                                        advanced: {
                                          ...currentComponent.chart_options?.options?.advanced,
                                          comparisonValue: name ? `:${name}` : ''
                                        }
                                      }
                                    }
                                  };
                                  setCurrentComponent(updated);
                                  setComponentJSON(JSON.stringify(updated, null, 2));
                                }}
                              />
                              <datalist id="pb-comparison-var-options">
                                {availablePublishVars.map((v) => (
                                  <option key={v} value={v} />
                                ))}
                              </datalist>
                              <Form.Text className="text-muted">
                                목록에서 선택하거나 직접 변수명을 입력할 수 있습니다. 저장 포맷: <code>:변수명</code>
                              </Form.Text>
                            </Form.Group>
                          )}

                          <Form.Group className="mb-3">
                            <Form.Label>비교값 라벨</Form.Label>
                            <Form.Control
                              type="text"
                              value={currentComponent?.chart_options?.options?.advanced?.comparisonLabel || ''}
                              onChange={(e) => {
                                if (!currentComponent) return;
                                const updatedComponent = {
                                  ...currentComponent,
                                  chart_options: {
                                    ...currentComponent.chart_options,
                                    options: {
                                      ...currentComponent.chart_options?.options,
                                      advanced: {
                                        ...currentComponent.chart_options?.options?.advanced,
                                        comparisonLabel: e.target.value
                                      }
                                    }
                                  }
                                };
                                setCurrentComponent(updatedComponent);
                                setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                              }}
                              placeholder="예: 목표값, 평균, 임계값"
                            />
                            <Form.Text className="text-muted">
                              비교값 위에 표시할 라벨을 입력하세요 (선택사항).
                            </Form.Text>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>비교선 색상</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                              <Form.Control
                                type="color"
                                value={currentComponent?.chart_options?.options?.advanced?.comparisonColor || '#ff4d4f'}
                                onChange={(e) => {
                                  if (!currentComponent) return;
                                  const updatedComponent = {
                                    ...currentComponent,
                                    chart_options: {
                                      ...currentComponent.chart_options,
                                      options: {
                                        ...currentComponent.chart_options?.options,
                                        advanced: {
                                          ...currentComponent.chart_options?.options?.advanced,
                                          comparisonColor: e.target.value
                                        }
                                      }
                                    }
                                  };
                                  setCurrentComponent(updatedComponent);
                                  setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                                }}
                                style={{ width: '60px' }}
                              />
                              <Form.Control
                                type="text"
                                value={currentComponent?.chart_options?.options?.advanced?.comparisonColor || '#ff4d4f'}
                                onChange={(e) => {
                                  if (!currentComponent) return;
                                  const updatedComponent = {
                                    ...currentComponent,
                                    chart_options: {
                                      ...currentComponent.chart_options,
                                      options: {
                                        ...currentComponent.chart_options?.options,
                                        advanced: {
                                          ...currentComponent.chart_options?.options?.advanced,
                                          comparisonColor: e.target.value
                                        }
                                      }
                                    }
                                  };
                                  setCurrentComponent(updatedComponent);
                                  setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                                }}
                                placeholder="#ff4d4f"
                                style={{ flex: 1 }}
                              />
                            </div>
                            <Form.Text className="text-muted">
                              비교값을 나타낼 선의 색상을 선택하세요.
                            </Form.Text>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>비교선 두께</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                              <Form.Range
                                min={1}
                                max={5}
                                value={currentComponent?.chart_options?.options?.advanced?.comparisonLineWidth || 2}
                                onChange={(e) => {
                                  if (!currentComponent) return;
                                  const updatedComponent = {
                                    ...currentComponent,
                                    chart_options: {
                                      ...currentComponent.chart_options,
                                      options: {
                                        ...currentComponent.chart_options?.options,
                                        advanced: {
                                          ...currentComponent.chart_options?.options?.advanced,
                                          comparisonLineWidth: parseInt(e.target.value)
                                        }
                                      }
                                    }
                                  };
                                  setCurrentComponent(updatedComponent);
                                  setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                                }}
                                className="flex-grow-1"
                              />
                              <span style={{ minWidth: '50px' }}>
                                {currentComponent?.chart_options?.options?.advanced?.comparisonLineWidth || 2}px
                              </span>
                            </div>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>비교선 스타일</Form.Label>
                            <Form.Select
                              value={currentComponent?.chart_options?.options?.advanced?.comparisonLineStyle || 'solid'}
                              onChange={(e) => {
                                if (!currentComponent) return;
                                const updatedComponent = {
                                  ...currentComponent,
                                  chart_options: {
                                    ...currentComponent.chart_options,
                                    options: {
                                      ...currentComponent.chart_options?.options,
                                      advanced: {
                                        ...currentComponent.chart_options?.options?.advanced,
                                        comparisonLineStyle: e.target.value
                                      }
                                    }
                                  }
                                };
                                setCurrentComponent(updatedComponent);
                                setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                              }}
                            >
                              <option value="solid">실선</option>
                              <option value="dashed">점선</option>
                              <option value="dotted">점점선</option>
                            </Form.Select>
                          </Form.Group>
                        </>
                      )}

                      <Form.Group className="mb-3">
                        <Form.Label>
                          바 높이
                          <Form.Text className="text-muted ms-2">
                            (기본값: 100%)
                          </Form.Text>
                        </Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Range
                            min={50}
                            max={500}
                            step={5}
                            value={progressBarHeight}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              setProgressBarHeight(value);
                              
                              // currentComponent에 바로 반영
                              if (currentComponent) {
                                const defaultHeight = 18; // 기본 높이 18px
                                const calculatedHeight = Math.round((defaultHeight * value) / 100);
                                
                                const updatedComponent = {
                                  ...currentComponent,
                                  chart_options: {
                                    ...currentComponent.chart_options,
                                    options: {
                                      ...currentComponent.chart_options?.options,
                                      advanced: {
                                        ...currentComponent.chart_options?.options?.advanced,
                                        barHeight: calculatedHeight,
                                        barHeightPercentage: value // 퍼센트 값도 저장
                                      }
                                    }
                                  }
                                };
                                
                                setCurrentComponent(updatedComponent);
                                setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                              }
                            }}
                            className="flex-grow-1"
                          />
                          <Form.Control
                            type="number"
                            min={50}
                            max={500}
                            value={progressBarHeight}
                            onChange={(e) => {
                              let value = parseInt(e.target.value) || 100;
                              // 범위 제한
                              value = Math.max(50, Math.min(500, value));
                              setProgressBarHeight(value);
                              
                              // currentComponent에 바로 반영
                              if (currentComponent) {
                                const defaultHeight = 18; // 기본 높이 18px
                                const calculatedHeight = Math.round((defaultHeight * value) / 100);
                                
                                const updatedComponent = {
                                  ...currentComponent,
                                  chart_options: {
                                    ...currentComponent.chart_options,
                                    options: {
                                      ...currentComponent.chart_options?.options,
                                      advanced: {
                                        ...currentComponent.chart_options?.options?.advanced,
                                        barHeight: calculatedHeight,
                                        barHeightPercentage: value // 퍼센트 값도 저장
                                      }
                                    }
                                  }
                                };
                                
                                setCurrentComponent(updatedComponent);
                                setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                              }
                            }}
                            style={{ width: '80px', marginLeft: '10px' }}
                          />
                          <span className="ms-2" style={{ fontWeight: 'bold' }}>%</span>
                        </div>
                        <Form.Text className="text-muted">
                          프로그레스바의 높이를 조정합니다. 100%가 기본 높이(18px)이며, 50%는 9px, 200%는 36px, 500%는 90px입니다.
                          <br />직접 입력하거나 슬라이더로 조정할 수 있습니다. (범위: 50% ~ 500%)
                        </Form.Text>
                      </Form.Group>
                      
                      {/* 추가 고유 옵션들 */}
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          id="show-scale"
                          label="눈금 표시"
                          checked={currentComponent?.chart_options?.options?.advanced?.showScale || false}
                          onChange={(e) => {
                            if (currentComponent) {
                              const updatedComponent = {
                                ...currentComponent,
                                chart_options: {
                                  ...currentComponent.chart_options,
                                  options: {
                                    ...currentComponent.chart_options?.options,
                                    advanced: {
                                      ...currentComponent.chart_options?.options?.advanced,
                                      showScale: e.target.checked
                                    }
                                  }
                                }
                              };
                              setCurrentComponent(updatedComponent);
                              setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                            }
                          }}
                        />
                        <Form.Text className="text-muted">
                          최소값, 중간값, 최대값 눈금을 표시합니다.
                        </Form.Text>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          id="show-details"
                          label="상태 텍스트 표시"
                          checked={currentComponent?.chart_options?.options?.advanced?.showDetails || false}
                          onChange={(e) => {
                            if (currentComponent) {
                              const updatedComponent = {
                                ...currentComponent,
                                chart_options: {
                                  ...currentComponent.chart_options,
                                  options: {
                                    ...currentComponent.chart_options?.options,
                                    advanced: {
                                      ...currentComponent.chart_options?.options?.advanced,
                                      showDetails: e.target.checked
                                    }
                                  }
                                }
                              };
                              setCurrentComponent(updatedComponent);
                              setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                            }
                          }}
                        />
                        <Form.Text className="text-muted">
                          현재 값에 따른 상태(위험/주의/보통/양호/우수)를 표시합니다.
                        </Form.Text>
                      </Form.Group>
                      
                      <Form.Group>
                        <Form.Label>바 모서리 반경</Form.Label>
                        <Form.Range
                          min={0}
                          max={30}
                          step={5}
                          value={currentComponent?.chart_options?.options?.advanced?.borderRadius || 15}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (currentComponent) {
                              const updatedComponent = {
                                ...currentComponent,
                                chart_options: {
                                  ...currentComponent.chart_options,
                                  options: {
                                    ...currentComponent.chart_options?.options,
                                    advanced: {
                                      ...currentComponent.chart_options?.options?.advanced,
                                      borderRadius: value
                                    }
                                  }
                                }
                              };
                              setCurrentComponent(updatedComponent);
                              setComponentJSON(JSON.stringify(updatedComponent, null, 2));
                            }
                          }}
                        />
                        <Form.Text className="text-muted">
                          바의 모서리 둥글기를 조정합니다. (0: 직각, 30: 매우 둥글게)
                        </Form.Text>
                      </Form.Group>
                    </div>
                  )}
                </div>
              </Tab.Pane>
            )}
            
            {isUpdateMode && (
              <Tab.Pane eventKey="events">
                <div className="events-editor-container" style={{ 
                  maxHeight: '450px', 
                  overflowY: 'auto'
                }}>
                  <EventTabPanel
                    config={eventConfig}
                    chartType={existingComponent?.chart_options?.viz_type || existingComponent?.type}
                    onChange={setEventConfig}
                    tableColumns={tableColumns}
                    sql={existingComponent?.data_source?.query || ''}
                  />
                </div>
              </Tab.Pane>
            )}
            
            <Tab.Pane eventKey="template-editor">
              <div className="template-editor-container">
                <div className="template-editor-header">
                  <h5>컴포넌트 템플릿 직접 편집</h5>
                  <div className="template-editor-actions">
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={formatCode}
                      disabled={loading}
                    >
                      포맷팅
                    </Button>
                  </div>
                </div>
                
                {jsonError && (
                  <Alert variant="danger" className="mt-2 mb-2">
                    {jsonError}
                  </Alert>
                )}
                
                <div className="template-editor" style={{ height: '580px', border: '1px solid #ced4da', borderRadius: '0.25rem' }}>
                  <Suspense fallback={<div className="editor-loading">에디터 로딩 중...</div>}>
                    <Editor
                      height="100%"
                      value={componentJSON}
                      onChange={handleTemplateEdit}
                      language="json"
                      theme="vs-dark"
                      options={{
                        minimap: { enabled: false },
                        scrollBeyondLastLine: true,
                        fontSize: 14,
                        formatOnPaste: true,
                        automaticLayout: false,
                        tabSize: 2,
                        wordWrap: "on",
                        padding: { top: 8, bottom: 64 }
                      }}
                      onMount={(editor) => {
                        editorRef.current = editor;
                        setTimeout(() => {
                          handleEditorResize();
                        }, 50);
                      }}
                      beforeMount={(monaco) => {
                        // JSON 스키마 설정
                        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                          validate: true,
                          schemas: [
                            {
                              uri: "http://example.com/component-template.json",
                              fileMatch: ["*"],
                              schema: componentTemplateSchema
                            }
                          ]
                        });
                      }}
                    />
                  </Suspense>
                </div>
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          취소
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!isSubmitButtonEnabled()}
        >
          {loading ? (
            <Spinner size="sm" animation="border" />
          ) : (
            isUpdateMode ? '컴포넌트 업데이트' : '컴포넌트 생성'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NLComponentModal; 